import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  TrendingUp,
  Menu, Package, Utensils,
  AlertTriangle, XCircle, Sparkles,
  ArrowLeft, Calendar, ChevronDown,
  Upload, FileSpreadsheet, Check, RotateCcw, FileText, Share2,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, StandardSearch } from '../styles/standardStyles';
import http from '../services/httpClient';
import { postUndo } from '../utils/ccsUndo';
import SettingsService from '../services/settingsService';
import MenuService from '../services/menuService';
import OnlineExportReportModal from './OnlineExportReportModal';
import NoDataPlaceholder from './NoDataPlaceholder';
import { formatSignedINR } from '../utils/signedMoney';
import { buildEditorialOnlinePdfBlob, fetchOnlineReportInput } from '../utils/onlineReportEditorialPdf';
import { onlineUnmappedItems, orderReportUnmappedByDate } from '../utils/onlineNotInMenu';
import './OnlineOrdersReport.css';

// Inline row for manually completing a truncated order (menu picker + qty + price)
function OrManualAddRow({ menuList, onAdd }) {
  const [name, setName] = useState('');
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState('');
  const submit = () => {
    if (!name.trim()) return;
    onAdd({ item_name: name.trim(), quantity: qty, price });
    setName(''); setQty(1); setPrice('');
  };
  return (
    <div className="oor-or-manual-row">
      <input
        list="or-menu-list"
        placeholder="Add missing item…"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          const m = menuList.find(x => (x.name || '').toLowerCase() === e.target.value.toLowerCase());
          if (m) setPrice(m.price);
        }}
        className="oor-or-manual-input"
        style={{ flex: 2 }}
      />
      <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="oor-or-manual-input" style={{ width: 48 }} title="Qty" />
      <input type="number" placeholder="₹" value={price} onChange={(e) => setPrice(e.target.value)} className="oor-or-manual-input" style={{ width: 68 }} title="Price" />
      <button type="button" className="oor-or-manual-add" onClick={submit}>Add</button>
    </div>
  );
}

const PERIOD_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

const formatDateParam = (date) => date.toLocaleDateString('en-CA');

const formatDateDisplayDMY = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const formatRangeDisplayDMY = (startDate, endDate) => (
  `${formatDateDisplayDMY(startDate)} - ${formatDateDisplayDMY(endDate)}`
);

const formatDateLabel = (dateString) => {
  if (!dateString) return '';
  const parsedDate = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return dateString;
  return parsedDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
};

const toTitleCase = (value) => {
  const safeValue = String(value ?? '').trim();
  if (!safeValue) return '';
  return safeValue
    .toLowerCase()
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
};

const getDateRangeForPeriod = (selectedPeriod, customStartDate, customEndDate) => {
  const today = new Date();
  const todayStr = formatDateParam(today);

  if (selectedPeriod === 'all') {
    return { startDate: null, endDate: null, label: 'All Time', isValid: true };
  }

  if (selectedPeriod === 'today') {
    return { startDate: todayStr, endDate: todayStr, label: 'Today', isValid: true };
  }

  if (selectedPeriod === 'week') {
    const start = new Date(today);
    const day = today.getDay();
    const diffToMonday = day === 0 ? 6 : day - 1;
    start.setDate(today.getDate() - diffToMonday);
    return {
      startDate: formatDateParam(start),
      endDate: todayStr,
      label: 'This Week',
      isValid: true,
    };
  }

  if (selectedPeriod === 'month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      startDate: formatDateParam(start),
      endDate: todayStr,
      label: 'This Month',
      isValid: true,
    };
  }

  if (selectedPeriod === 'year') {
    const start = new Date(today.getFullYear(), 0, 1);
    return {
      startDate: formatDateParam(start),
      endDate: todayStr,
      label: 'This Year',
      isValid: true,
    };
  }

  if (selectedPeriod === 'custom') {
    if (!customStartDate || !customEndDate) {
      return {
        startDate: customStartDate || null,
        endDate: customEndDate || null,
        label: 'Custom Range',
        isValid: false,
        validationMessage: 'Select both start and end date for custom range.',
      };
    }

    if (customEndDate < customStartDate) {
      return {
        startDate: customStartDate,
        endDate: customEndDate,
        label: 'Custom Range',
        isValid: false,
        validationMessage: 'End date must be on or after start date.',
      };
    }

    return {
      startDate: customStartDate,
      endDate: customEndDate,
      label: `${formatDateLabel(customStartDate)} – ${formatDateLabel(customEndDate)}`,
      isValid: true,
    };
  }

  return { startDate: null, endDate: null, label: 'All Time', isValid: true };
};

const computeBepBreakdown = (amount, period, forDate = null) => {
  const num = Number(amount) || 0;
  let monthly = 0;
  const weeksPerYear = 52.1429;
  const daysPerYear = 365.2425;

  switch ((period || 'Month').toLowerCase()) {
    case 'year':
      monthly = num / 12;
      break;
    case 'week':
      monthly = num * (weeksPerYear / 12);
      break;
    case 'day':
      monthly = num * (daysPerYear / 12);
      break;
    default:
      monthly = num;
      break;
  }

  const basisDate = forDate || new Date();
  const monthDays = new Date(basisDate.getFullYear(), basisDate.getMonth() + 1, 0).getDate();

  return {
    daily: monthDays > 0 ? monthly / monthDays : 0,
    weekly: monthly / 4,
    monthly,
    yearly: monthly * 12,
  };
};

const getContributionLabel = (selectedPeriod) => {
  if (selectedPeriod === 'week') return 'Weekly Contribution';
  if (selectedPeriod === 'month') return 'Monthly Contribution';
  if (selectedPeriod === 'year') return 'Yearly Contribution';
  return 'Daily Contribution';
};

const ITEM_IMAGE_BASE_URL = 'https://storage.googleapis.com/menuimages001';
const ITEM_IMAGE_FALLBACK = `${ITEM_IMAGE_BASE_URL}/itemmap/default.png`;

const resolveItemImageUrl = (rawImageUrl) => {
  if (typeof rawImageUrl !== 'string') return '';

  const normalized = rawImageUrl.trim();
  if (!normalized) return '';

  if (/^https?:\/\//i.test(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }

  if (normalized.startsWith('/uploads')) {
    return `${ITEM_IMAGE_BASE_URL}${normalized}`;
  }

  if (normalized.startsWith('/')) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${normalized}`;
    }
    return normalized;
  }

  return `${ITEM_IMAGE_BASE_URL}/${normalized.replace(/^\/+/, '')}`;
};

const OnlineDatePickerCombined = ({ isOpen, onClose, onRangeSelect }) => {
  const [pickerMode, setPickerMode] = useState('dates');
  const [dateMode, setDateMode] = useState('single');
  const [viewMonth, setViewMonth] = useState(new Date());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const currentToday = new Date();
  const currentYear = currentToday.getFullYear();
  const currentMonth = currentToday.getMonth();
  const isFutureMonthNav = viewMonth.getFullYear() > currentYear || 
      (viewMonth.getFullYear() === currentYear && viewMonth.getMonth() >= currentMonth);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const getWeekStart = (date) => {
    const value = new Date(date);
    const day = value.getDay();
    const diff = day === 0 ? 6 : day - 1;
    value.setDate(value.getDate() - diff);
    value.setHours(0, 0, 0, 0);
    return value;
  };

  const getWeekEnd = (date) => {
    const value = new Date(date);
    const day = value.getDay();
    const diff = day === 0 ? 0 : 7 - day;
    value.setDate(value.getDate() + diff);
    value.setHours(0, 0, 0, 0);
    return value;
  };

  const navigateMonth = (direction) => {
    const nextMonth = new Date(viewMonth);
    nextMonth.setMonth(viewMonth.getMonth() + direction);
    setViewMonth(nextMonth);
  };

  const navigateYear = (direction) => {
    setViewYear((previousYear) => previousYear + direction);
  };

  const getWeeksInMonth = () => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const weeks = [];
    let currentWeekStart = getWeekStart(firstDay);

    while (currentWeekStart <= lastDay) {
      const weekEnd = getWeekEnd(currentWeekStart);
      const weekStartDate = new Date(currentWeekStart);
      const weekEndDate = new Date(weekEnd);

      if (weekEndDate >= firstDay && weekStartDate <= lastDay) {
        weeks.push({ start: new Date(weekStartDate), end: new Date(weekEndDate) });
      }

      currentWeekStart = new Date(currentWeekStart);
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }

    return weeks;
  };

  const getDaysInMonth = () => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const days = [];
    for (let index = 0; index < adjustedStart; index += 1) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const emitSelection = (startDate, endDate, label, meta = {}) => {
    onRangeSelect({ startDate, endDate, label, ...meta });
  };

  const handleWeekClick = (week) => {
    emitSelection(week.start, week.end, formatRangeDisplayDMY(week.start, week.end), {
      selectionType: 'week',
      isRangeMode: false,
    });
    onClose();
  };

  const handleMonthClick = (monthIndex) => {
    const monthStart = new Date(viewYear, monthIndex, 1);
    const monthEnd = new Date(viewYear, monthIndex + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const boundedEnd = monthEnd > today ? today : monthEnd;

    emitSelection(monthStart, boundedEnd, `${monthNames[monthIndex]} ${viewYear}`, {
      selectionType: 'month',
      isRangeMode: false,
    });
    onClose();
  };

  const handleDateClick = (date) => {
    if (!date) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today) return;

    if (dateMode === 'single') {
      emitSelection(date, date, formatDateDisplayDMY(date), {
        selectionType: 'single-date',
        isRangeMode: false,
      });
      onClose();
      return;
    }

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      return;
    }

    if (date < selectedStartDate) {
      setSelectedEndDate(selectedStartDate);
      setSelectedStartDate(date);
      return;
    }

    setSelectedEndDate(date);
  };

  const applyDateRange = () => {
    if (!selectedStartDate || !selectedEndDate) return;

    emitSelection(selectedStartDate, selectedEndDate, formatRangeDisplayDMY(selectedStartDate, selectedEndDate), {
      selectionType: 'date-range',
      isRangeMode: true,
    });
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    onClose();
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !date) return false;
    if (!selectedEndDate) return date.getTime() === selectedStartDate.getTime();
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const renderWeekGrid = () => {
    const today = new Date();
    const thisWeekStart = getWeekStart(today);
    const weeks = getWeeksInMonth();

    return (
      <div className="oor-dp-week-list">
        {weeks.map((week, index) => {
          const isThisWeek = week.start.getTime() === thisWeekStart.getTime();
          const isFutureWeek = week.start > today;

          return (
            <button
              key={`oor-week-${index}`}
              type="button"
              className={`oor-dp-week-cell ${isThisWeek ? 'current' : ''} ${isFutureWeek ? 'disabled' : ''}`}
              onClick={() => !isFutureWeek && handleWeekClick(week)}
              disabled={isFutureWeek}
            >
              {isThisWeek && '✓ '}
              {formatRangeDisplayDMY(week.start, week.end)}
            </button>
          );
        })}
      </div>
    );
  };

  const renderMonthGrid = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    return (
      <div className="oor-dp-month-grid">
        {Array.from({ length: 12 }).map((_, monthIndex) => {
          const isCurrentMonth = monthIndex === currentMonth && viewYear === currentYear;
          const isFutureMonth = viewYear > currentYear || (viewYear === currentYear && monthIndex > currentMonth);

          return (
            <button
              key={`oor-month-${monthIndex}`}
              type="button"
              className={`oor-dp-month-cell ${isCurrentMonth ? 'current' : ''} ${isFutureMonth ? 'future' : ''}`}
              onClick={() => !isFutureMonth && handleMonthClick(monthIndex)}
              disabled={isFutureMonth}
            >
              {monthNames[monthIndex].substring(0, 3)}
            </button>
          );
        })}
      </div>
    );
  };

  const renderDateCalendar = () => {
    const days = getDaysInMonth();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div className="oor-dp-date-wrap">
        <div className="oor-dp-date-header">
          <div className="oor-dp-month-nav">
            <button type="button" className="oor-dp-nav-btn" onClick={() => navigateMonth(-1)}>‹</button>
            <span>{monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
            <button 
              type="button" 
              className="oor-dp-nav-btn" 
              onClick={() => navigateMonth(1)}
              disabled={isFutureMonthNav}
              style={isFutureMonthNav ? {
                cursor: 'not-allowed',
                color: '#ccc',
                opacity: 0.5
              } : undefined}
            >›</button>
          </div>

          <button
            type="button"
            className={`oor-dp-range-toggle ${dateMode === 'range' ? 'active' : ''}`}
            onClick={() => {
              setDateMode((mode) => (mode === 'single' ? 'range' : 'single'));
              setSelectedStartDate(null);
              setSelectedEndDate(null);
            }}
          >
            Range Mode
          </button>
        </div>

        <div className="oor-dp-weekdays">
          {dayNames.map((day) => (
            <div key={day} className="oor-dp-weekday">{day}</div>
          ))}
        </div>

        <div className="oor-dp-days-grid">
          {days.map((date, index) => {
            if (!date) {
              return <div key={`oor-empty-${index}`} />;
            }

            const highlightToday = dateMode === 'single' && date.getTime() === today.getTime();
            const inRange = isDateInRange(date);
            const isFuture = date > today;

            return (
              <button
                key={`oor-day-${index}`}
                type="button"
                className={`oor-dp-day ${highlightToday ? 'today' : ''} ${inRange ? 'in-range' : ''} ${isFuture ? 'future' : ''}`}
                onClick={() => handleDateClick(date)}
                disabled={isFuture}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>

        {selectedStartDate && selectedEndDate && (
          <div className="oor-dp-apply-row">
            <button type="button" className="oor-dp-apply-btn" onClick={applyDateRange}>
              Apply Date Range
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="oor-dp-overlay" onClick={onClose}>
      <div className="oor-dp-popup" onClick={(event) => event.stopPropagation()}>
        <div className="oor-dp-tabs">
          <button
            type="button"
            className={`oor-dp-tab ${pickerMode === 'dates' ? 'active' : ''}`}
            onClick={() => setPickerMode('dates')}
          >
            Dates
          </button>
          <button
            type="button"
            className={`oor-dp-tab ${pickerMode === 'week' ? 'active' : ''}`}
            onClick={() => setPickerMode('week')}
          >
            Week
          </button>
          <button
            type="button"
            className={`oor-dp-tab ${pickerMode === 'month' ? 'active' : ''}`}
            onClick={() => setPickerMode('month')}
          >
            Month
          </button>
        </div>

        {pickerMode === 'month' && (
          <div className="oor-dp-header">
            <button type="button" className="oor-dp-nav-btn" onClick={() => navigateYear(-1)}>‹</button>
            <h3>{viewYear}</h3>
            <button 
              type="button" 
              className="oor-dp-nav-btn" 
              onClick={() => navigateYear(1)}
              disabled={viewYear >= currentYear}
              style={viewYear >= currentYear ? {
                cursor: 'not-allowed',
                color: '#ccc',
                opacity: 0.5
              } : undefined}
            >›</button>
          </div>
        )}

        {pickerMode === 'week' && (
          <div className="oor-dp-header">
            <button type="button" className="oor-dp-nav-btn" onClick={() => navigateMonth(-1)}>‹</button>
            <h3>{monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}</h3>
            <button 
              type="button" 
              className="oor-dp-nav-btn" 
              onClick={() => navigateMonth(1)}
              disabled={isFutureMonthNav}
              style={isFutureMonthNav ? {
                cursor: 'not-allowed',
                color: '#ccc',
                opacity: 0.5
              } : undefined}
            >›</button>
          </div>
        )}

        {pickerMode === 'week' ? renderWeekGrid() : pickerMode === 'month' ? renderMonthGrid() : renderDateCalendar()}
      </div>
    </div>
  );
};

const OnlinePeriodFilter = ({
  selectedPeriod,
  triggerLabel,
  onPeriodChange,
  customOptionLabel,
  onCalendarRangeSelect,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const handleSelect = (periodKey) => {
    onPeriodChange(periodKey);
    setMenuOpen(false);
  };

  const handleCalendarClick = () => {
    setMenuOpen(false);
    setShowDatePicker(true);
  };

  return (
    <div className="oor-sales-date-picker" ref={menuRef}>
      <button
        type="button"
        className="oor-sales-calendar-btn"
        onClick={handleCalendarClick}
        aria-label="Open period options"
      >
        <Calendar size={18} />
      </button>

      <button
        type="button"
        className="oor-sales-period-trigger"
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span className="oor-sales-period-label">{triggerLabel}</span>
        <span className={`oor-sales-chevron ${menuOpen ? 'open' : ''}`} />
      </button>

      {menuOpen && (
        <div className="oor-sales-period-menu">
          <div className="oor-sales-select-list">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`oor-sales-period-option ${selectedPeriod === option.key ? 'active' : ''}`}
                onClick={() => handleSelect(option.key)}
              >
                {option.label}
              </button>
            ))}

            {customOptionLabel && (
              <button
                type="button"
                className={`oor-sales-period-option ${selectedPeriod === 'custom' ? 'active' : ''}`}
                onClick={() => handleSelect('custom')}
              >
                {customOptionLabel}
              </button>
            )}
          </div>
        </div>
      )}

      <OnlineDatePickerCombined
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onRangeSelect={(selection) => {
          onCalendarRangeSelect(selection);
          setShowDatePicker(false);
        }}
      />
    </div>
  );
};

const OnlineReportTopHeader = ({ goTo }) => {
  return (
    <header className="oor-sales-report-header">
      <div className="oor-sales-header-row">
        <button
          type="button"
          onClick={() => goTo && goTo('reports')}
          className="oor-sales-back-btn"
          aria-label="Back to Reports"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="oor-sales-header-title">Online Sales Reports</h1>
      </div>
    </header>
  );
};

/* ================================================================
   PulseHeader - Now with dynamic data
   ================================================================ */
const PulseHeader = ({ dashboardData, selectedPeriod, bepBasisDate, onTotalSalesClick }) => {
  const [fixedMonthlyExpense, setFixedMonthlyExpense] = useState(null);
  const [bepModalOpen, setBepModalOpen] = useState(false);
  const [bepAmount, setBepAmount] = useState('');
  const [bepPeriod, setBepPeriod] = useState('Month');
  const [bepPeriodOpen, setBepPeriodOpen] = useState(false);
  const [bepBreakdown, setBepBreakdown] = useState({ daily: 0, weekly: 0, monthly: 0, yearly: 0 });
  const [isSavingBep, setIsSavingBep] = useState(false);

  // `totalSales` is NOT the gross that Total Sales was netted down from. It is the sum of
  // the MENU price of everything sold online — a reference value written at import so the
  // platform's price can be compared against the menu — while `finalSales` is the realised
  // platform revenue (final selling price × qty), already net of the funded discount. The
  // two sit on different price bases, so subtracting the discount from one does not produce
  // the other: for July 2026 the card printed "₹4,02,916 − ₹19,807 = ₹4,50,022", an identity
  // that is off by ₹66,913 in both directions at once.
  //
  // The gross is therefore rebuilt on the SAME basis as the figure it explains.
  const menuPriceValue = Number(dashboardData?.totalSales || 0);
  const lostToDiscount = dashboardData?.totalDiscount || 0;
  const totalOnlineEarnings = Number(
    dashboardData?.finalSales != null ? dashboardData.finalSales : menuPriceValue - lostToDiscount
  ) || 0;
  const grossOnlineSales = totalOnlineEarnings + lostToDiscount;
  const grossProfit = dashboardData?.grossProfit || 0;
  // When an item sold this period is in negative stock, COGS/GP is unreliable, so the
  // backend flags it and the Gross Profit card shows "-" instead of a figure.
  const grossProfitUnavailable = Boolean(dashboardData?.grossProfitUnavailable);
  const basisDateValue = bepBasisDate || formatDateParam(new Date());
  const parsedBasisDate = new Date(`${basisDateValue}T00:00:00`);
  const normalizedBasisDate = Number.isNaN(parsedBasisDate.getTime()) ? new Date() : parsedBasisDate;
  const normalizedBasisDateParam = formatDateParam(normalizedBasisDate);

  const currentTargetBreakdown = computeBepBreakdown(
    fixedMonthlyExpense || 0,
    'Month',
    normalizedBasisDate,
  );
  const contributionTarget = selectedPeriod === 'week'
    ? currentTargetBreakdown.weekly
    : selectedPeriod === 'month'
      ? currentTargetBreakdown.monthly
      : selectedPeriod === 'year'
        ? currentTargetBreakdown.yearly
        : currentTargetBreakdown.daily;

  useEffect(() => {
    let isMounted = true;

    const fetchBepSetting = async () => {
      try {
        const businessId = Number(localStorage.getItem('businessId'));
        if (!businessId) {
          if (isMounted) setFixedMonthlyExpense(null);
          return;
        }

        const response = await http.get(`/reports/daily-contribution?date=${normalizedBasisDateParam}&businessId=${businessId}`);
        const payload = response?.data || response || {};
        const monthlyValue = Number(payload.fixed_monthly_expenses || 0);

        if (isMounted) {
          setFixedMonthlyExpense(Number.isFinite(monthlyValue) ? monthlyValue : 0);
        }
      } catch (error) {
        console.error('Failed to fetch BEP setting for online report:', error);
        if (isMounted) {
          setFixedMonthlyExpense(null);
        }
      }
    };

    fetchBepSetting();
    return () => {
      isMounted = false;
    };
  }, [normalizedBasisDateParam]);

  useEffect(() => {
    if (!bepModalOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setBepModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [bepModalOpen]);

  const saveBepSetting = async () => {
    try {
      setIsSavingBep(true);
      const businessId = Number(localStorage.getItem('businessId'));
      if (!businessId) return;

      const breakdown = computeBepBreakdown(bepAmount, bepPeriod, normalizedBasisDate);
      const monthlyValue = Number(breakdown.monthly || 0);

      const payload = [{
        key: 'owner.fixed_monthly_expenses',
        value: monthlyValue,
        data_type: 'number',
        module_scope: 'finance',
      }];

      await SettingsService.upsertSettings(businessId, payload);
      setFixedMonthlyExpense(monthlyValue);
      setBepBreakdown(computeBepBreakdown(monthlyValue, 'Month', normalizedBasisDate));
      setBepPeriodOpen(false);
      setBepModalOpen(false);
    } catch (error) {
      console.error('Failed to save BEP setting for online report:', error);
    } finally {
      setIsSavingBep(false);
    }
  };

  const cards = [
    {
      key: 'totalSales',
      label: 'Total Sales',
      value: formatSignedINR(totalOnlineEarnings, { decimals: Number.isInteger(totalOnlineEarnings) ? 0 : 2 }),
    },
    {
      key: 'lostToDiscount',
      label: 'Lost to Discount',
      value: `₹${lostToDiscount.toLocaleString('en-IN')}`,
      // Platform list price − funded discount = what the customer actually paid. All three
      // figures are on the realised basis, so the line genuinely adds up.
      detail: lostToDiscount > 0
        ? `₹${grossOnlineSales.toLocaleString('en-IN')} − ₹${lostToDiscount.toLocaleString('en-IN')} = ₹${totalOnlineEarnings.toLocaleString('en-IN')}`
        : null,
    },
    {
      key: 'grossProfit',
      // A loss reads as a loss, as the TSR report's "Net Loss" card does.
      label: !grossProfitUnavailable && grossProfit < 0 ? 'Gross Loss' : 'Gross Profit',
      value: grossProfitUnavailable ? '-' : formatSignedINR(grossProfit, { decimals: Number.isInteger(grossProfit) ? 0 : 2 }),
      negative: !grossProfitUnavailable && grossProfit < 0,
    },
    {
      key: 'dailyContribution',
      label: getContributionLabel(selectedPeriod),
      value: `₹${Number(contributionTarget || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
      // The card shows the target the period must cover, not money earned.
      detail: 'break-even target',
    },
  ];

  return (
    <div className="oor-header">
      <div className="oor-sales-stats-grid">
        {cards.map((m, i) => {
          const isDailyCard = m.key === 'dailyContribution';
          const isTotalSalesCard = m.key === 'totalSales';
          const isDetailCard = isTotalSalesCard;
          const openBepModal = () => {
            const monthly = fixedMonthlyExpense ? Number(fixedMonthlyExpense) : 0;
            setBepAmount(fixedMonthlyExpense ? String(fixedMonthlyExpense) : '');
            setBepPeriod('Month');
            setBepPeriodOpen(false);
            setBepBreakdown(computeBepBreakdown(monthly, 'Month', normalizedBasisDate));
            setBepModalOpen(true);
          };

          return (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`oor-sales-stat-card ${(isDailyCard || isDetailCard) ? 'oor-sales-stat-card-clickable' : ''}`}
              onClick={isDetailCard ? onTotalSalesClick : isDailyCard ? openBepModal : undefined}
              role={(isDailyCard || isDetailCard) ? 'button' : undefined}
              tabIndex={(isDailyCard || isDetailCard) ? 0 : undefined}
              aria-haspopup={(isDailyCard || isDetailCard) ? 'dialog' : undefined}
              aria-expanded={isDailyCard ? bepModalOpen : undefined}
              onKeyDown={isDetailCard
                ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onTotalSalesClick && onTotalSalesClick();
                  }
                }
                : isDailyCard
                ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openBepModal();
                  }
                }
                : undefined}
            >
              <p
                className="oor-sales-card-title"
                style={{ color: 'black', ...(isDetailCard ? { marginBottom: '0.25rem' } : {}) }}
              >
                {m.label}
              </p>
              <p
                className="oor-sales-card-value"
                style={{
                  ...(isDetailCard ? { marginBottom: '-0.25rem' } : {}),
                  ...(m.negative ? { color: '#dc2626' } : {}),
                }}
              >
                {m.value}
              </p>

              {isDetailCard && (
                <p style={{ fontSize: 9, color: '#aaa', margin: 0, marginTop: 4, lineHeight: '1.2' }}>
                  Click to view details
                </p>
              )}

              {m.detail && (
                <p style={{ fontSize: 11, color: '#888', margin: 0, marginTop: 4, lineHeight: '1.2', fontWeight: 600 }}>
                  {m.detail}
                </p>
              )}

              {isDailyCard && fixedMonthlyExpense === null && (
                <p style={{ marginTop: 8, fontSize: 12, lineHeight: '1.2' }}>
                  Click to set BEP
                </p>
              )}
            </motion.div>
          );
        })}
      </div>

      {bepModalOpen && (
        <div className="oor-bep-modal-overlay" onClick={() => {
          setBepPeriodOpen(false);
          setBepModalOpen(false);
        }}>
          <div
            className="oor-bep-modal-container"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Set Break-Even Point Target"
          >
            <h3 className="oor-bep-modal-title">Set Break-Even Point Target</h3>
            <p className="oor-bep-modal-subtitle">Enter your total estimated expenses</p>

            <div className="oor-bep-input-row">
              <div className="oor-bep-amount-input">
                <span style={{ fontSize: 18 }}>₹</span>
                <input
                  type="number"
                  value={bepAmount}
                  onChange={(event) => {
                    const value = event.target.value;
                    setBepAmount(value);
                    setBepBreakdown(computeBepBreakdown(value, bepPeriod, normalizedBasisDate));
                  }}
                  placeholder="Amount"
                />
              </div>

              <div className="oor-bep-period-selector">
                <button
                  type="button"
                  onClick={() => setBepPeriodOpen((open) => !open)}
                  className="oor-bep-period-button"
                >
                  <span>{bepPeriod}</span>
                  <span className="oor-bep-period-arrow" />
                </button>

                {bepPeriodOpen && (
                  <div className="oor-bep-period-dropdown">
                    {['Month', 'Year', 'Week', 'Day'].map((option) => (
                      <div
                        key={option}
                        onClick={() => {
                          setBepPeriod(option);
                          setBepBreakdown(computeBepBreakdown(bepAmount, option, normalizedBasisDate));
                          setBepPeriodOpen(false);
                        }}
                        className={`oor-bep-period-option ${bepPeriod === option ? 'active' : ''}`}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="oor-bep-breakdown-box">
              <strong className="oor-bep-breakdown-title">BEP Targets Breakdown:</strong>
              <div className="oor-bep-breakdown-content">
                <div className="oor-bep-breakdown-grid">
                  <div className="oor-bep-breakdown-item left">Daily: ₹{(bepBreakdown.daily || 0).toFixed(0)}</div>
                  <div className="oor-bep-breakdown-item right">Monthly: ₹{(bepBreakdown.monthly || 0).toFixed(0)}</div>
                  <div className="oor-bep-breakdown-item left">Weekly: ₹{(bepBreakdown.weekly || 0).toFixed(0)}</div>
                  <div className="oor-bep-breakdown-item right">Yearly: ₹{(bepBreakdown.yearly || 0).toFixed(0)}</div>
                </div>
              </div>
            </div>

            <div className="oor-bep-button-row">
              <button
                onClick={() => {
                  setBepPeriodOpen(false);
                  setBepModalOpen(false);
                }}
                className="oor-bep-button oor-bep-button-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveBepSetting}
                disabled={isSavingBep}
                className="oor-bep-button oor-bep-button-save"
              >
                {isSavingBep ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================================================================
   ItemSalesReport - Now with dynamic data
   ================================================================ */
const isPlaceholderImage = (url) => {
  if (!url) return true;
  return url.includes('default.png') || url.includes('placeholder') || url.includes('itemmap/default.png');
};

const ItemAvatar = ({
  name,
  imageUrl,
  imageClassName = 'oor-item-image',
  iconClassName = 'oor-item-icon',
}) => {
  const resolvedImageUrl = resolveItemImageUrl(imageUrl);
  const [currentSrc, setCurrentSrc] = useState(resolvedImageUrl);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setCurrentSrc(resolvedImageUrl);
    setHasError(false);
  }, [resolvedImageUrl]);

  const isPlaceholder = isPlaceholderImage(currentSrc);

  if (!currentSrc || hasError || isPlaceholder) {
    return (
      <div className={iconClassName}>
        <Utensils size={18} />
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={name || 'Menu item'}
      className={imageClassName}
      loading="lazy"
      onError={() => {
        if (currentSrc === ITEM_IMAGE_FALLBACK) {
          setHasError(true);
        } else {
          setCurrentSrc(ITEM_IMAGE_FALLBACK);
        }
      }}
    />
  );
};

// Margin health band → left color strip + pill color.
//   green >40% · amber 15–40% · red <15% or negative · gray = unknown (no recipe)
const marginBandOf = (item) => {
  if (item.margin === null || item.margin === undefined) return 'unknown';
  if (item.margin < 15) return 'low';
  if (item.margin < 40) return 'mid';
  return 'high';
};

/**
 * Why a dish shows no margin. "No recipe found" used to be shown for every dish
 * without a known cost — including dishes WITH a recipe whose cost could not be
 * worked out (an ingredient in negative stock, or never priced), which sent
 * owners off to add a recipe that already existed.
 */
const costBannerFor = (item) => {
  if (!item || item.cogsKnown) return null;
  if (!item.hasRecipe) return 'no_recipe';
  return item.grossProfitUnavailable ? 'cost_unavailable' : null;
};

/**
 * One row per dish for the "All" chip.
 *
 * /item-wise-report answers per platform (the PDF splits channels from those
 * rows), so under "All" a dish sold on Zomato and Swiggy arrived twice — two
 * rows with one name, and the dish's whole wastage on each. Quantities and
 * money add; wastage is the dish's, not the platform's, so it is taken once;
 * per-unit figures and the margin are recomputed from the merged sums; and the
 * dish's cost is known only if every part of it was.
 */
const mergeItemsAcrossPlatforms = (items = []) => {
  const groups = new Map();
  const order = [];
  items.forEach((it) => {
    const key = it.menuItemId != null ? `id:${it.menuItemId}` : `name:${String(it.name || '').toLowerCase()}`;
    if (!groups.has(key)) { groups.set(key, []); order.push(key); }
    groups.get(key).push(it);
  });

  return order.map((key) => {
    const parts = groups.get(key);
    if (parts.length === 1) return parts[0];
    const sum = (f) => parts.reduce((s, p) => s + (Number(p[f]) || 0), 0);
    const max = (f) => parts.reduce((m, p) => Math.max(m, Number(p[f]) || 0), 0);
    const qtySold = sum('qtySold');
    const totalSale = sum('totalSale');
    const grossMenuTotal = sum('grossMenuTotal');
    const grossProfit = sum('grossProfit');
    const cogsKnown = parts.every((p) => p.cogsKnown);
    return {
      ...parts[0],
      channel: null,
      qtySold,
      totalSale,
      finalSales: totalSale,
      grossMenuTotal,
      totalDiscount: sum('totalDiscount'),
      totalCOGS: sum('totalCOGS'),
      grossProfit,
      orderCount: sum('orderCount'),
      price: qtySold > 0 ? totalSale / qtySold : 0,
      menuUnitPrice: qtySold > 0 ? grossMenuTotal / qtySold : parts[0].menuUnitPrice,
      // The dish's wastage, repeated on each platform row — counted once.
      wastageCost: max('wastageCost'),
      wastagePlates: max('wastagePlates'),
      wastageCount: max('wastageCount'),
      cogsKnown,
      hasRecipe: parts.some((p) => p.hasRecipe),
      grossProfitUnavailable: parts.some((p) => p.grossProfitUnavailable),
      margin: cogsKnown && totalSale > 0 ? Number(((grossProfit / totalSale) * 100).toFixed(1)) : null,
    };
  });
};

const ITEM_SORTS = [
  { key: 'profit', label: 'Top profit' },
  { key: 'margin', label: 'Top margin' },
  { key: 'qty', label: 'Most sold' },
  { key: 'sales', label: 'Top sales' },
  { key: 'wastage', label: 'Most wastage' },
];

const ItemSalesReport = ({ itemsData, isLoading }) => {
  const [showItemWiseModal, setShowItemWiseModal] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [sortKey, setSortKey] = useState('profit');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  const inr = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  const inr2 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Derive sorted rows (with rank + GP-contribution %) and period totals once.
  const { rows, totals } = useMemo(() => {
    const base = (itemsData || []).map((item) => ({
      ...item,
      displayName: toTitleCase(item.name),
    }));

    const knownSales = base.reduce((s, i) => s + (i.cogsKnown ? (i.totalSale || 0) : 0), 0);
    const totalGP = base.reduce((s, i) => s + (i.cogsKnown ? (i.grossProfit || 0) : 0), 0);
    const gpPool = base.reduce((s, i) => s + (i.cogsKnown && i.grossProfit > 0 ? i.grossProfit : 0), 0);
    const totalsObj = {
      qty: base.reduce((s, i) => s + (i.qtySold || 0), 0),
      sales: base.reduce((s, i) => s + (i.totalSale || 0), 0),
      cogs: base.reduce((s, i) => s + (i.cogsKnown ? (i.totalCOGS || 0) : 0), 0),
      gp: totalGP,
      wastage: base.reduce((s, i) => s + (i.wastageCost || 0), 0),
      blendedMargin: knownSales > 0 ? (totalGP / knownSales * 100) : null,
      noRecipeCount: base.filter((i) => !i.cogsKnown).length,
      itemCount: base.length,
    };

    const sorters = {
      profit: (a, b) => (b.cogsKnown ? b.grossProfit : -Infinity) - (a.cogsKnown ? a.grossProfit : -Infinity),
      margin: (a, b) => (b.margin === null ? -Infinity : b.margin) - (a.margin === null ? -Infinity : a.margin),
      qty: (a, b) => (b.qtySold || 0) - (a.qtySold || 0),
      sales: (a, b) => (b.totalSale || 0) - (a.totalSale || 0),
      wastage: (a, b) => (b.wastageCost || 0) - (a.wastageCost || 0),
    };
    const sorted = [...base].sort(sorters[sortKey] || sorters.profit);
    const withRank = sorted.map((item, idx) => ({
      ...item,
      rank: idx + 1,
      contribPct: gpPool > 0 && item.cogsKnown && item.grossProfit > 0
        ? Math.max(0, Math.min(100, item.grossProfit / gpPool * 100))
        : 0,
    }));

    return { rows: withRank, totals: totalsObj };
  }, [itemsData, sortKey]);

  const hasItemData = rows.length > 0;
  const activeSort = ITEM_SORTS.find((s) => s.key === sortKey) || ITEM_SORTS[0];

  const closeItemWiseModal = () => {
    setShowItemWiseModal(false);
  };

  const openItemWiseModal = () => {
    setShowItemWiseModal(true);
  };

  // Reconciled per-order / per-unit money ladder shown inside an expanded row.
  const renderGpBreakdown = (item) => {
    const qty = item.qtySold || 0;
    const menuUnit = Number(item.menuUnitPrice || item.price || 0);
    const discUnit = qty > 0 ? Number(item.totalDiscount || 0) / qty : 0;
    const netUnit = Number(item.price || 0);
    const cogsUnit = Number(item.cogsPerUnit || 0);
    const gpUnit = netUnit - cogsUnit;
    const cogsKnown = item.cogsKnown;
    const belowCost = cogsKnown && gpUnit < 0;
    const discShare = item.grossMenuTotal > 0 ? (Number(item.totalDiscount || 0) / item.grossMenuTotal) : 0;

    return (
      <div className="oor-itemwise-gp-breakdown">
        {/* ── Anomaly / data-quality banners ─────────────────────── */}
        {costBannerFor(item) === 'no_recipe' && (
          <div className="oor-gp-flag warn">
            <AlertTriangle size={14} />
            <span>No recipe found — cost &amp; margin can't be calculated. Add a recipe to see profit.</span>
          </div>
        )}
        {costBannerFor(item) === 'cost_unavailable' && (
          <div className="oor-gp-flag warn">
            <AlertTriangle size={14} />
            <span>Cost can't be calculated — an ingredient is in negative stock or has no price yet.</span>
          </div>
        )}
        {belowCost && (
          <div className="oor-gp-flag danger">
            <AlertTriangle size={14} />
            <span>Selling below cost — losing ₹{Math.abs(gpUnit).toFixed(2)}/unit.</span>
          </div>
        )}
        {cogsKnown && !belowCost && discShare > 0.15 && (
          <div className="oor-gp-flag warn">
            <AlertTriangle size={14} />
            <span>Heavy discounting — sold {(discShare * 100).toFixed(0)}% below menu price.</span>
          </div>
        )}

        {/* ── Per-unit card ──────────────────────────────────────── */}
        <div className="oor-gp-formula">
          <h5>Gross Profit — Per Unit</h5>
          <div className="oor-gp-formula-rows">
            <div className="oor-gp-row">
              <span>Menu Price / unit</span>
              <span className="oor-gp-val">₹{inr2(menuUnit)}</span>
            </div>
            {discUnit > 0 && (
              <div className="oor-gp-row">
                <span>Discount / unit</span>
                <span className="oor-gp-val negative">− ₹{inr2(discUnit)}</span>
              </div>
            )}
            <div className="oor-gp-row oor-gp-row-sub">
              <span>Net Selling / unit</span>
              <span className="oor-gp-val">₹{inr2(netUnit)}</span>
            </div>
            <div className="oor-gp-row">
              <span>COGS / unit {item.hasRecipe ? '(recipe)' : ''}</span>
              <span className={`oor-gp-val ${cogsKnown ? 'negative' : 'neutral'}`}>
                {cogsKnown ? `− ₹${inr2(cogsUnit)}` : 'not tracked'}
              </span>
            </div>
            <div className="oor-gp-row oor-gp-row-result">
              <span>GP / unit</span>
              <span className={`oor-gp-val ${!cogsKnown ? 'neutral' : (gpUnit >= 0 ? 'positive' : 'negative')}`}>
                {!cogsKnown ? '—' : formatSignedINR(gpUnit, { decimals: 2 })}
              </span>
            </div>
            <div className="oor-gp-row oor-gp-row-result">
              <span>GP %</span>
              <span className={`oor-gp-val ${item.margin === null ? 'neutral' : (item.margin >= 0 ? 'positive' : 'negative')}`}>
                {item.margin === null ? '—' : `${item.margin}%`}
              </span>
            </div>
          </div>
          {cogsKnown && netUnit > 0 && (
            <div className="oor-gp-math-formula">
              <span>GP% = (</span>
              <span className="oor-gp-math-val">₹{inr2(netUnit)}</span>
              <span> − </span>
              <span className="oor-gp-math-val negative">₹{inr2(cogsUnit)}</span>
              <span>) / </span>
              <span className="oor-gp-math-val">₹{inr2(netUnit)}</span>
              <span> × 100 = </span>
              <span className={`oor-gp-math-val ${item.margin >= 0 ? 'positive' : 'negative'}`}>
                {item.margin}%
              </span>
            </div>
          )}
        </div>

        {/* ── Total (× qty) card ─────────────────────────────────── */}
        <div className="oor-gp-totals">
          <h5>Total · {qty} Sold</h5>
          <div className="oor-gp-formula-rows">
            <div className="oor-gp-row">
              <span>Gross (menu)</span>
              <span className="oor-gp-val">₹{inr2(item.grossMenuTotal)}</span>
            </div>
            {Number(item.totalDiscount || 0) > 0 && (
              <div className="oor-gp-row">
                <span>Discount</span>
                <span className="oor-gp-val negative">− ₹{inr2(item.totalDiscount)}</span>
              </div>
            )}
            <div className="oor-gp-row oor-gp-row-sub">
              <span>Total Sales</span>
              <span className="oor-gp-val">₹{inr2(item.totalSale)}</span>
            </div>
            <div className="oor-gp-row">
              <span>Total COGS</span>
              <span className={`oor-gp-val ${cogsKnown ? 'negative' : 'neutral'}`}>
                {cogsKnown ? `− ₹${inr2(item.totalCOGS)}` : 'not tracked'}
              </span>
            </div>
            {Number(item.wastageCost || 0) > 0 && (
              <div className="oor-gp-row">
                <span>Wastage cost</span>
                <span className="oor-gp-val negative">− ₹{inr2(item.wastageCost)}</span>
              </div>
            )}
            <div className="oor-gp-row oor-gp-row-result">
              <span>Gross Profit</span>
              <span className={`oor-gp-val ${!cogsKnown ? 'neutral' : (item.grossProfit >= 0 ? 'positive' : 'negative')}`}>
                {!cogsKnown ? '—' : formatSignedINR(item.grossProfit, { decimals: 2 })}
              </span>
            </div>
          </div>
        </div>

        {item.ingredientsUsed && item.ingredientsUsed.length > 0 && (
          <div className="oor-gp-ingredients">
            <h5>Recipe Ingredients</h5>
            <table className="oor-gp-ing-table">
              <thead>
                <tr>
                  <th className="oor-gp-ing-th-name">Ingredients</th>
                  <th className="oor-gp-ing-th-qty">Quantity</th>
                  <th className="oor-gp-ing-th-amt">Amount</th>
                </tr>
              </thead>
              <tbody>
                {item.ingredientsUsed.map((ing, idx) => (
                  <tr key={idx} className="oor-gp-ing-row">
                    <td className="oor-gp-ing-name">{ing.name}</td>
                    <td className="oor-gp-ing-qty">{Number(ing.quantity).toFixed(2)} {ing.unit}</td>
                    <td className="oor-gp-ing-cost">₹{Number(ing.cost || 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="oor-gp-ing-row oor-gp-ing-subtotal">
                  <td className="oor-gp-ing-name">Recipe subtotal</td>
                  <td className="oor-gp-ing-qty"></td>
                  <td className="oor-gp-ing-cost">₹{inr2(item.recipeCost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {item.complimentaryIngredients && item.complimentaryIngredients.length > 0 && (
          <div className="oor-gp-ingredients">
            <h5>Complimentary Items</h5>
            <table className="oor-gp-ing-table">
              <thead>
                <tr>
                  <th className="oor-gp-ing-th-name">Item</th>
                  <th className="oor-gp-ing-th-qty">Quantity</th>
                  <th className="oor-gp-ing-th-amt">Amount</th>
                </tr>
              </thead>
              <tbody>
                {item.complimentaryIngredients.map((ci, idx) => (
                  <tr key={idx} className="oor-gp-ing-row">
                    <td className="oor-gp-ing-name">{ci.name}</td>
                    <td className="oor-gp-ing-qty">{Number(ci.quantity).toFixed(2)} {ci.unit}</td>
                    <td className="oor-gp-ing-cost">₹{Number(ci.cost || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {item.preparationAdjustments && item.preparationAdjustments.length > 0 && (
          <div className="oor-gp-ingredients">
            <h5>Preparation Adjustments</h5>
            <table className="oor-gp-ing-table">
              <thead>
                <tr>
                  <th className="oor-gp-ing-th-name">Preparation</th>
                  <th className="oor-gp-ing-th-qty">Components</th>
                  <th className="oor-gp-ing-th-amt">Amount</th>
                </tr>
              </thead>
              <tbody>
                {item.preparationAdjustments.map((pa, idx) => (
                  <tr key={idx} className="oor-gp-ing-row">
                    <td className="oor-gp-ing-name">{pa.name}</td>
                    <td className="oor-gp-ing-qty">{pa.componentCount} items</td>
                    <td className="oor-gp-ing-cost">₹{Number(pa.cost || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <motion.div className="oor-card">
        <div className="oor-card-header">
          <div className="oor-card-title-row">
            <Package className="oor-icon" />
            <h3 className="oor-card-title">Item-wise Sales & Wastage Analysis</h3>
          </div>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading item data...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="oor-metric-card oor-stock-item-sales-card"
        style={{ marginBottom: '1.5rem' }}
      >
        <div className="oor-stock-item-sales-inner">
          <div className="oor-stock-item-sales-header">
            <h2 className="oor-stock-item-sales-title">Item-Wise Sales</h2>
            {hasItemData && (
              <div className="oor-itemwise-header-controls">
                <div className="oor-itemwise-sort" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="oor-itemwise-sort-pill"
                    onClick={() => setSortMenuOpen((v) => !v)}
                  >
                    {activeSort.label}
                    <ChevronDown size={13} className={sortMenuOpen ? 'rotated' : ''} />
                  </button>
                  {sortMenuOpen && (
                    <div className="oor-itemwise-sort-menu">
                      {ITEM_SORTS.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          className={`oor-itemwise-sort-opt ${s.key === sortKey ? 'active' : ''}`}
                          onClick={() => { setSortKey(s.key); setSortMenuOpen(false); }}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="oor-stock-item-sales-menu-btn"
                  aria-label="Open item-wise table"
                  onClick={openItemWiseModal}
                >
                  <Menu size={18} color="#6b7280" />
                </button>
              </div>
            )}
          </div>

          {/* ── Level 1 · Summary strip ─────────────────────────── */}
          {hasItemData && (
            <div className="oor-itemwise-summary">
              <div className="oor-itemwise-chip">
                <span className="oor-itemwise-chip-val">{inr(totals.qty)}</span>
                <span className="oor-itemwise-chip-label">sold</span>
              </div>
              <div className="oor-itemwise-chip">
                <span className="oor-itemwise-chip-val">₹{inr(totals.sales)}</span>
                <span className="oor-itemwise-chip-label">sales</span>
              </div>
              <div className="oor-itemwise-chip">
                <span className={`oor-itemwise-chip-val ${totals.gp >= 0 ? 'pos' : 'neg'}`}>{formatSignedINR(totals.gp)}</span>
                <span className="oor-itemwise-chip-label">
                  gross profit{totals.blendedMargin !== null ? ` · ${totals.blendedMargin.toFixed(0)}%` : ''}
                </span>
                {/* This total, and the margin beside it, cover only the items whose cost is
                    known — both the sum and its denominator skip anything uncosted. Without
                    saying so it reads as the whole period's profit, and sits directly under a
                    Gross Profit card showing "-" for the very same period. Two answers to one
                    question, on one screen, is what makes a report untrustworthy. */}
                {totals.noRecipeCount > 0 && (
                  <span className="oor-itemwise-chip-note">
                    excludes {totals.noRecipeCount} uncosted item{totals.noRecipeCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className={`oor-stock-card-content-scrollable ${!hasItemData ? 'oor-stock-card-content-empty' : ''}`}>
            {!hasItemData ? (
              <NoDataPlaceholder
                type="sales"
                title="No sales data available"
                subtitle="Item-wise online sales breakdown will appear here once orders are recorded."
                minHeight="220px"
              />
            ) : (
              rows.map((item, i) => {
                const band = marginBandOf(item);
                return (
                <motion.div
                  key={item.menuItemId || `${item.displayName}-${i}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.03 * i }}
                  className={`oor-stock-item-row-new oor-band-${band}`}
                >
                  <div className="oor-stock-item-row-left">
                    <ItemAvatar
                      name={item.displayName}
                      imageUrl={item.imageUrl}
                      imageClassName="oor-stock-item-image-new"
                      iconClassName="oor-stock-item-icon"
                    />
                    <div className="oor-stock-item-details-new">
                      <h4 className="oor-stock-item-name-new">{item.displayName}</h4>
                      <p className="oor-stock-item-price-new">
                        ₹{Number(item.price || 0).toFixed(2)}/unit • {item.qtySold} sold
                      </p>
                      {item.wastageCost > 0 && (
                        <p className="oor-stock-item-wastage-new alert">
                          Wastage: {item.wastagePlates} Plates (₹{item.wastageCost.toLocaleString('en-IN')})
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="oor-stock-item-profit-new">
                    <p className="oor-stock-item-profit-value-new">
                      <span className="oor-stock-currency-symbol">₹</span>
                      <span className="oor-stock-profit-amount">{(item.totalSale || 0).toLocaleString('en-IN')}</span>
                    </p>
                    <p className={`oor-stock-item-profit-change-new oor-pill-${band}`}>
                      {item.margin === null ? '—' : `${item.margin}%`}
                    </p>
                  </div>
                </motion.div>
                );
              })
            )}
          </div>
        </div>
      </motion.div>

      {showItemWiseModal && (
        <div className="oor-itemwise-modal-overlay" onClick={closeItemWiseModal}>
          <div className="oor-itemwise-modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="oor-itemwise-modal-header">
              <h2>Item-Wise Sales Report</h2>
              <button
                type="button"
                onClick={closeItemWiseModal}
                className="oor-itemwise-modal-close"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="oor-itemwise-modal-body">
              <div className="oor-itemwise-modal-list">
                {!hasItemData ? (
                  <NoDataPlaceholder
                    type="sales"
                    title="No sales data available"
                    subtitle="No online order data available for the selected period."
                    minHeight="220px"
                  />
                ) : (
                  rows.map((item, index) => {
                    const itemKey = item.menuItemId || `${item.displayName}-${index}`;
                    const isExpanded = expandedItemId === itemKey;
                    const band = marginBandOf(item);
                    return (
                    <div
                      key={itemKey}
                      className="oor-itemwise-modal-row-wrapper"
                    >
                      <div
                        className={`oor-itemwise-modal-row oor-band-${band} ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => setExpandedItemId(isExpanded ? null : itemKey)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="oor-itemwise-modal-row-left">
                          <ItemAvatar
                            name={item.displayName}
                            imageUrl={item.imageUrl}
                            imageClassName="oor-itemwise-modal-image"
                            iconClassName="oor-itemwise-modal-image"
                          />
                          <div className="oor-itemwise-modal-details">
                            <h4 style={{textAlign:'left'}}>{item.displayName}</h4>
                            <p style={{textAlign:'left'}}>₹{Number(item.price || 0).toFixed(2)}/unit • {item.qtySold} Sold</p>
                            {item.wastageCost > 0 && (
                              <p className="oor-itemwise-modal-wastage alert" style={{textAlign:'left'}}>
                                Wastage: {item.wastagePlates} Plates (₹{Number(item.wastageCost || 0).toLocaleString('en-IN')})
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="oor-itemwise-modal-right-group">
                          <div className="oor-itemwise-modal-profit">
                            <p className="oor-itemwise-modal-profit-value">
                              ₹{Number(item.totalSale || 0).toLocaleString('en-IN')}
                            </p>
                            <p className={`oor-itemwise-modal-profit-change oor-pill-${band}`}>
                              {item.margin === null ? '—' : `${item.margin}%`}
                            </p>
                          </div>
                          <ChevronDown
                            size={16}
                            className={`oor-itemwise-chevron ${isExpanded ? 'rotated' : ''}`}
                          />
                        </div>
                      </div>

                      {isExpanded && renderGpBreakdown(item)}
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ================================================================
   SalesBarChart - Now with dynamic data
   ================================================================ */
const SALES_REPORT_BAR_BLUE = '#2563eb';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="oor-tooltip">
      <p className="oor-tooltip-label">{label}</p>
      <p className="oor-tooltip-value">
        ₹{Number(payload[0].value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
      </p>
    </div>
  );
};

const formatCurrencyAxisValue = (value) => {
  const numericValue = Number(value || 0);
  return `₹${numericValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const getNiceTickStep = (maxValue, targetTickCount = 5) => {
  const safeMaxValue = Math.max(0, Number(maxValue || 0));
  if (safeMaxValue <= 0) return 100;

  const roughStep = safeMaxValue / Math.max(1, targetTickCount);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const residual = roughStep / magnitude;

  if (residual > 5) return 10 * magnitude;
  if (residual > 2) return 5 * magnitude;
  if (residual > 1) return 2 * magnitude;
  return magnitude;
};

const buildCurrencyTicks = (values, targetTickCount = 5) => {
  const numericValues = (values || []).map((value) => Number(value || 0));
  const maxValue = Math.max(0, ...numericValues);
  const tickStep = getNiceTickStep(maxValue, targetTickCount);
  const maxTick = Math.max(tickStep, Math.ceil(maxValue / tickStep) * tickStep);

  const ticks = [];
  for (let tickValue = 0; tickValue <= maxTick; tickValue += tickStep) {
    ticks.push(tickValue);
  }

  return ticks.length ? ticks : [0, tickStep];
};

const toDateKey = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDateParam(parsed);
};

const dateFromDateKey = (dateKey) => {
  if (!dateKey) return null;
  const parsed = new Date(`${dateKey}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getWeekStartDateKey = (dateKey) => {
  const date = dateFromDateKey(dateKey);
  if (!date) return '';

  const day = date.getDay();
  const diffToMonday = day === 0 ? 6 : day - 1;
  date.setDate(date.getDate() - diffToMonday);

  return formatDateParam(date);
};

const getMonthStartDateKey = (dateKey) => {
  const date = dateFromDateKey(dateKey);
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
};

const formatMonthLabel = (dateKey) => {
  const date = dateFromDateKey(dateKey);
  if (!date) return dateKey;
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const formatDayBucketLabel = (dateKey, includeWeekday) => {
  const date = dateFromDateKey(dateKey);
  if (!date) return dateKey;

  return date.toLocaleDateString('en-US', includeWeekday
    ? { weekday: 'short', month: 'short', day: '2-digit' }
    : { month: 'short', day: '2-digit' });
};

const formatWeekRangeLabel = (startDateKey, endDateKey) => {
  const start = dateFromDateKey(startDateKey);
  const end = dateFromDateKey(endDateKey || startDateKey);

  if (!start || !end) {
    return startDateKey || endDateKey || '';
  }

  const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${start.toLocaleDateString('en-US', { month: 'short' })} ${String(start.getDate()).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })}-${end.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })}`;
  }

  return `${start.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: '2-digit' })}-${end.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: '2-digit' })}`;
};

const getRangeDayCount = (startDate, endDate, fallbackCount) => {
  const start = dateFromDateKey(toDateKey(startDate));
  const end = dateFromDateKey(toDateKey(endDate));

  if (!start || !end) {
    return Math.max(1, Number(fallbackCount || 1));
  }

  const minDate = start <= end ? start : end;
  const maxDate = end >= start ? end : start;
  const diffMs = maxDate.getTime() - minDate.getTime();
  return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1);
};

const getTrendAggregationForPeriod = (selectedPeriod, rangeDayCount) => {
  const normalizedPeriod = (selectedPeriod || '').toLowerCase();

  if (normalizedPeriod === 'year') return 'month';
  if (normalizedPeriod === 'month') return 'week';
  if (normalizedPeriod === 'week') return 'date';
  if (normalizedPeriod === 'today') return 'date';

  if (normalizedPeriod === 'custom' || normalizedPeriod === 'all') {
    if (rangeDayCount > 120) return 'month';
    if (rangeDayCount > 35) return 'week';
    return 'date';
  }

  if (rangeDayCount > 120) return 'month';
  if (rangeDayCount > 35) return 'week';
  return 'date';
};

const buildRangeDailyBreakdown = (normalizedDailyBreakdown, startDate, endDate) => {
  if (!startDate || !endDate) {
    return normalizedDailyBreakdown.map((entry) => ({
      dateKey: entry.dateKey,
      totalSales: Number(entry.totalSales || 0),
      totalCOGS: Number(entry.totalCOGS || 0),
      totalWastage: Number(entry.totalWastage || 0),
      totalDiscount: Number(entry.totalDiscount || 0),
      totalOrders: Number(entry.totalOrders || 0),
    }));
  }

  const startKey = toDateKey(startDate);
  const endKey = toDateKey(endDate);
  const start = dateFromDateKey(startKey);
  const end = dateFromDateKey(endKey);

  if (!start || !end) {
    return normalizedDailyBreakdown.map((entry) => ({
      dateKey: entry.dateKey,
      totalSales: Number(entry.totalSales || 0),
      totalCOGS: Number(entry.totalCOGS || 0),
      totalWastage: Number(entry.totalWastage || 0),
      totalDiscount: Number(entry.totalDiscount || 0),
      totalOrders: Number(entry.totalOrders || 0),
    }));
  }

  const normalizedStart = start <= end ? start : end;
  const normalizedEnd = end >= start ? end : start;

  const breakdownByDate = new Map(
    normalizedDailyBreakdown.map((entry) => [entry.dateKey, entry])
  );

  const filledBreakdown = [];
  const cursor = new Date(normalizedStart);

  while (cursor <= normalizedEnd) {
    const currentDateKey = formatDateParam(cursor);
    const sourceEntry = breakdownByDate.get(currentDateKey);

    filledBreakdown.push({
      dateKey: currentDateKey,
      totalSales: Number(sourceEntry?.totalSales || 0),
      totalCOGS: Number(sourceEntry?.totalCOGS || 0),
      totalWastage: Number(sourceEntry?.totalWastage || 0),
      totalDiscount: Number(sourceEntry?.totalDiscount || 0),
      totalOrders: Number(sourceEntry?.totalOrders || 0),
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return filledBreakdown;
};

const formatDateKeyDayMonth = (dateKey) => {
  const date = dateFromDateKey(dateKey);
  if (!date) return dateKey;
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const OnlineDateWiseBreakdownTable = ({ rows }) => {
  const safeRows = Array.isArray(rows) ? rows : [];

  if (!safeRows.length) {
    return <p className="oor-date-breakdown-empty">No date-wise data available for this range.</p>;
  }

  const totals = safeRows.reduce((acc, row) => ({
    totalSales: acc.totalSales + Number(row.totalSales || 0),
    totalCOGS: acc.totalCOGS + Number(row.totalCOGS || 0),
    totalWastage: acc.totalWastage + Number(row.totalWastage || 0),
  }), {
    totalSales: 0,
    totalCOGS: 0,
    totalWastage: 0,
  });

  return (
    <div className="oor-date-breakdown-wrap">
      <h3 className="oor-date-breakdown-title">Date-wise Breakdown</h3>
      <div className="oor-date-breakdown-table-wrap">
        <table className="oor-date-breakdown-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left', paddingLeft: '1rem' }}>DATE</th>
              <th className="oor-align-center">TOTAL SALE</th>
              <th className="oor-align-center">EXPENSE</th>
              <th className="oor-align-center">WASTAGE</th>
            </tr>
          </thead>
          <tbody>
            {safeRows.map((row, index) => (
              <tr key={row.dateKey || `oor-breakdown-${index}`}>
                <td style={{ textAlign: 'left', paddingLeft: '1rem' }}>{formatDateKeyDayMonth(row.dateKey)}</td>
                <td className="oor-align-center oor-date-sale">{formatCurrencyAxisValue(row.totalSales)}</td>
                <td className="oor-align-center oor-date-expense">{formatCurrencyAxisValue(row.totalCOGS)}</td>
                <td className="oor-align-center oor-date-wastage">{formatCurrencyAxisValue(row.totalWastage)}</td>
              </tr>
            ))}
            <tr className="oor-date-breakdown-total-row">
              <td style={{ textAlign: 'left', paddingLeft: '1rem' }}>TOTAL</td>
              <td className="oor-align-center oor-date-sale-total">{formatCurrencyAxisValue(totals.totalSales)}</td>
              <td className="oor-align-center oor-date-expense">{formatCurrencyAxisValue(totals.totalCOGS)}</td>
              <td className="oor-align-center oor-date-wastage">{formatCurrencyAxisValue(totals.totalWastage)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RenderYAxisTick = (props) => {
  const { x, y, payload } = props;
  const label = payload.value || '';
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  
  const maxLineLen = isMobile ? 11 : 16;
  const fontSize = isMobile ? 10 : 12;
  const lineHeight = isMobile ? 12 : 14;
  
  // Split label into words
  const words = label.split(' ');
  const lines = [];
  let currentLine = '';
  words.forEach(word => {
    if ((currentLine + ' ' + word).trim().length <= maxLineLen) {
      currentLine = (currentLine + ' ' + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);

  const finalLines = lines.slice(0, 2);
  if (lines.length > 2) {
    finalLines[1] = finalLines[1].slice(0, maxLineLen - 3) + '...';
  }

  const lineCount = finalLines.length;
  const startY = y - ((lineCount - 1) * lineHeight) / 2 + 4;

  return (
    <g>
      {finalLines.map((line, idx) => (
        <text
          key={idx}
          x={x - 6}
          y={startY + (idx * lineHeight)}
          textAnchor="end"
          fill="hsl(220 10% 40%)"
          fontSize={fontSize}
          fontWeight={500}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const SalesBarChart = ({
  dailyBreakdown,
  itemsData,
  isLoading,
  selectedPeriod,
  showDateWiseBreakdownTable,
  periodLabel,
  periodStartDate,
  periodEndDate,
}) => {
  const [view, setView] = useState('item');
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 600 : false;
  // `totalSales` off the wire is SUM(OnlineOrderTransactions.total_amount) — the MENU price
  // of what was sold, a reference value written at import. `finalSales` is
  // SUM(final_amount): what the customer actually paid on the platform, already net of the
  // funded discount. Only the second is revenue.
  //
  // Charting the first is what made this screen disagree with itself: the Total Sales card
  // read ₹4,50,022.12 (realised) while the chart directly beneath it totalled ₹4,02,916.26
  // (menu price) for the same July. Same screen, same period, two price bases, ₹47,105.86
  // apart. The backend already treats finalSales as the revenue basis — it computes each
  // day's grossProfit as finalSales − COGS — so the chart was the lone holdout.
  //
  // Normalised once, here, because the trend chart, the week/month rollups and the
  // Date-wise Breakdown table all read this same array.
  const realisedSales = (day) => {
    const final = Number(day.finalSales ?? day.final_sales ?? NaN);
    if (Number.isFinite(final) && final > 0) return final;
    // Pre-final_amount rows: fall back the same way the rest of the app does.
    const menu = Number(day.totalSales || day.total_sales || 0);
    const discount = Number(day.totalDiscount || day.total_discount || 0);
    return Math.max(menu - discount, 0);
  };

  const normalizedDailyBreakdown = [...(dailyBreakdown || [])]
    .map((day) => ({
      dateKey: toDateKey(day.date || day.orderDate || day.order_date),
      totalSales: realisedSales(day),
      totalCOGS: Number(day.totalCOGS || day.total_cogs || 0),
      totalWastage: Number(day.totalWastage || day.total_wastage || 0),
      totalDiscount: Number(day.totalDiscount || day.total_discount || 0),
      totalOrders: Number(day.totalOrders || day.total_orders || 0),
    }))
    .filter((day) => day.dateKey)
    .sort((firstDay, secondDay) => firstDay.dateKey.localeCompare(secondDay.dateKey));

  const chartDailyBreakdown = buildRangeDailyBreakdown(
    normalizedDailyBreakdown,
    periodStartDate,
    periodEndDate,
  );
  const rangeDayCount = getRangeDayCount(periodStartDate, periodEndDate, chartDailyBreakdown.length);
  const normalizedPeriodLabel = periodLabel || 'Selected Period';
  const isSingleDaySelection = Boolean(
    periodStartDate
    && periodEndDate
    && toDateKey(periodStartDate)
    && toDateKey(periodStartDate) === toDateKey(periodEndDate)
  );
  const showTrendTab = !isSingleDaySelection;
  const resolvedView = showTrendTab ? view : 'item';
  const trendAggregation = showTrendTab
    ? (showDateWiseBreakdownTable ? 'date' : getTrendAggregationForPeriod(selectedPeriod, rangeDayCount))
    : 'date';
  const shouldRenderDateWiseTable = Boolean(showTrendTab && showDateWiseBreakdownTable && resolvedView === 'trend');

  useEffect(() => {
    setView('item');
  }, [selectedPeriod, periodStartDate, periodEndDate, showDateWiseBreakdownTable, showTrendTab]);

  // Prepare day data (top items from today)
  const dayData = [...(itemsData || [])]
    .sort((a, b) => b.totalSale - a.totalSale)
    .slice(0, 8)
    .map(item => ({ name: toTitleCase(item.name), sale: item.totalSale }));

  // Prepare date-wise data for selected range.
  const dateWiseData = chartDailyBreakdown.map((day) => {
    const label = formatDayBucketLabel(day.dateKey, chartDailyBreakdown.length <= 14);

    return {
      name: label,
      sale: Number(day.totalSales || 0),
    };
  });

  // Prepare week-wise data for selected range.
  const weeklyBuckets = new Map();
  chartDailyBreakdown.forEach((day) => {
    const weekStartKey = getWeekStartDateKey(day.dateKey);
    if (!weekStartKey) return;

    const current = weeklyBuckets.get(weekStartKey) || {
      startDateKey: day.dateKey,
      endDateKey: day.dateKey,
      sale: 0,
    };

    const normalizedDateKey = day.dateKey;
    if (normalizedDateKey < current.startDateKey) {
      current.startDateKey = normalizedDateKey;
    }
    if (normalizedDateKey > current.endDateKey) {
      current.endDateKey = normalizedDateKey;
    }

    current.sale += Number(day.totalSales || 0);
    weeklyBuckets.set(weekStartKey, current);
  });

  const weekWiseData = Array.from(weeklyBuckets.entries())
    .sort(([leftWeekStart], [rightWeekStart]) => leftWeekStart.localeCompare(rightWeekStart))
    .map(([, weekBucket]) => ({
      name: formatWeekRangeLabel(weekBucket.startDateKey, weekBucket.endDateKey),
      sale: Number(weekBucket.sale || 0),
    }));

  const monthlyBuckets = new Map();
  chartDailyBreakdown.forEach((day) => {
    const monthStartKey = getMonthStartDateKey(day.dateKey);
    if (!monthStartKey) return;

    const previous = Number(monthlyBuckets.get(monthStartKey) || 0);
    monthlyBuckets.set(monthStartKey, previous + Number(day.totalSales || 0));
  });

  const monthWiseData = Array.from(monthlyBuckets.entries())
    .sort(([leftMonthStart], [rightMonthStart]) => leftMonthStart.localeCompare(rightMonthStart))
    .map(([monthStartKey, sale]) => ({
      name: formatMonthLabel(monthStartKey),
      sale,
    }));

  const trendConfig = showDateWiseBreakdownTable
    ? {
      buttonLabel: 'Date-wise',
      subtitle: `Date-wise breakdown for ${normalizedPeriodLabel}`,
      totalLabel: `${normalizedPeriodLabel} Total`,
      performerNoun: 'date',
      chartData: dateWiseData,
    }
    : trendAggregation === 'month'
    ? {
      buttonLabel: 'Month-wise',
      subtitle: `Month-wise sales for ${normalizedPeriodLabel}`,
      totalLabel: `${normalizedPeriodLabel} Total`,
      performerNoun: 'month',
      chartData: monthWiseData,
    }
    : trendAggregation === 'week'
      ? {
        buttonLabel: 'Week-wise',
        subtitle: `Week-wise sales for ${normalizedPeriodLabel}`,
        totalLabel: `${normalizedPeriodLabel} Total`,
        performerNoun: 'week',
        chartData: weekWiseData,
      }
      : {
        buttonLabel: 'Date-wise',
        subtitle: `Date-wise sales for ${normalizedPeriodLabel}`,
        totalLabel: `${normalizedPeriodLabel} Total`,
        performerNoun: 'date',
        chartData: dateWiseData,
      };

  const viewConfig = showTrendTab
    ? {
      item: {
        buttonLabel: 'Item-wise',
        subtitle: `Item-wise sales for ${normalizedPeriodLabel}`,
        totalLabel: 'Item-wise Total',
        performerNoun: 'item',
        chartData: dayData,
      },
      trend: trendConfig,
    }
    : {
      item: {
        buttonLabel: 'Item-wise',
        subtitle: `Item-wise sales for ${normalizedPeriodLabel}`,
        totalLabel: 'Item-wise Total',
        performerNoun: 'item',
        chartData: dayData,
      },
    };

  const activeViewConfig = showTrendTab
    ? (viewConfig[resolvedView] || viewConfig.item)
    : viewConfig.item;

  const chartData = activeViewConfig.chartData;
  const total = chartData.reduce((s, d) => s + d.sale, 0);
  const isItemView = resolvedView === 'item';
  const axisTicks = buildCurrencyTicks(
    chartData.map((entry) => Number(entry.sale || 0)),
    isMobile ? 4 : 5,
  );
  const axisMax = axisTicks[axisTicks.length - 1] || 0;
  const axisLabelWidth = Math.min(
    120,
    Math.max(isMobile ? 56 : 70, formatCurrencyAxisValue(axisMax).length * (isMobile ? 6 : 7)),
  );
  const hasSalesData = chartData.some((entry) => Number(entry.sale || 0) > 0);
  const shouldShowSalesEmptyState = !hasSalesData;
  const topPerformer = hasSalesData
    ? chartData.reduce((currentMax, currentEntry) =>
        currentEntry.sale > currentMax.sale ? currentEntry : currentMax
      )
    : null;
  const trendBarSize = isMobile
    ? (trendAggregation === 'month' ? 28 : trendAggregation === 'week' ? 34 : 24)
    : (trendAggregation === 'month' ? 40 : trendAggregation === 'week' ? 48 : 36);

  if (isLoading) {
    return (
      <motion.div className="oor-metric-card oor-sales-overview-card" style={{ marginBottom: '1.5rem' }}>
        <div className="oor-chart-header">
          <h2 className="oor-section-title">Sales Overview</h2>
        </div>
        <div className="oor-sales-overview-empty">
          <p>Loading sales data...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="oor-metric-card oor-sales-overview-card"
      style={{ marginBottom: '1.5rem' }}
    >
      <div className="oor-chart-header">
        <div>
          <h2 className="oor-section-title">Sales Overview</h2>
        </div>
        {!shouldShowSalesEmptyState && (
          <div className="oor-view-toggle">
            {(showTrendTab
              ? [
                { key: 'item', label: viewConfig.item.buttonLabel },
                { key: 'trend', label: viewConfig.trend.buttonLabel },
              ]
              : [
                { key: 'item', label: viewConfig.item.buttonLabel },
              ]
            ).map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`oor-view-btn ${resolvedView === v.key ? 'active' : ''}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {shouldShowSalesEmptyState ? (
        <NoDataPlaceholder
          type="usage"
          title="No sales overview data"
          subtitle="Sales trends and overview charts will appear here for the selected period."
          minHeight="240px"
        />
      ) : !shouldRenderDateWiseTable ? (
        <div className="oor-sales-overview-content">
          <div className="oor-chart-total">
            <p className="oor-chart-total-label">{activeViewConfig.totalLabel}</p>
            <p className="oor-chart-total-value">
              ₹{total.toLocaleString('en-IN')}
            </p>
          </div>

          <div className="oor-sales-chart-area" style={{ overflowY: isItemView ? 'auto' : 'hidden' }}>
            <div style={{ height: isItemView ? `${Math.max(220, chartData.length * 42)}px` : '100%', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                {isItemView ? (
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    barCategoryGap="20%"
                    margin={{ left: isMobile ? -12 : 10, right: isMobile ? 8 : 40 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke="hsl(220 14% 92%)" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: 'hsl(220 10% 52%)', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      ticks={axisTicks}
                      domain={[0, axisMax]}
                      allowDecimals={false}
                      tickFormatter={formatCurrencyAxisValue}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={<RenderYAxisTick />}
                      axisLine={false}
                      tickLine={false}
                      width={isMobile ? 88 : 120}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(220 14% 96%)' }} />
                    <Bar dataKey="sale" radius={[0, 6, 6, 0]} barSize={24}>
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={SALES_REPORT_BAR_BLUE} opacity={0.85} />
                      ))}
                      <LabelList
                        dataKey="sale"
                        position="right"
                        formatter={formatCurrencyAxisValue}
                        style={{ fill: 'hsl(220 10% 40%)', fontSize: 12, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                ) : (
                  <BarChart
                    data={chartData}
                    barCategoryGap="25%"
                    margin={{ left: isMobile ? -10 : 0, right: isMobile ? 0 : 10, bottom: isMobile ? 28 : 0 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke="hsl(220 14% 92%)" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: 'hsl(220 10% 40%)', fontSize: 12, fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                      angle={isMobile ? -25 : 0}
                      textAnchor={isMobile ? 'end' : 'middle'}
                      height={isMobile ? 56 : 30}
                      minTickGap={isMobile ? 4 : 10}
                    />
                    <YAxis
                      tick={{ fill: 'hsl(220 10% 52%)', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      ticks={axisTicks}
                      domain={[0, axisMax]}
                      allowDecimals={false}
                      tickFormatter={formatCurrencyAxisValue}
                      width={axisLabelWidth}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(220 14% 96%)' }} />
                    <Bar
                      dataKey="sale"
                      radius={[6, 6, 0, 0]}
                      barSize={trendBarSize}
                    >
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={SALES_REPORT_BAR_BLUE} opacity={0.85} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div className="oor-sales-overview-table-wrap">
          <OnlineDateWiseBreakdownTable rows={chartDailyBreakdown} />
        </div>
      )}
    </motion.div>
  );
};

/* ================================================================
   ItemPerformanceTable - Now with dynamic data
   ================================================================ */
const ItemPerformanceTable = ({ itemsData, isLoading }) => {
  // Transform itemsData to perfItems format
  const perfItems = (itemsData || []).map(item => {
    const margin = item.margin;
    const costPerUnit = item.qtySold > 0 ? (item.totalCOGS / item.qtySold) : 0;

    return {
      item: toTitleCase(item.name),
      qty: item.qtySold,
      price: item.price,
      cost: Math.round(costPerUnit),
      margin: margin,
    };
  });

  if (isLoading) {
    return (
      <motion.div className="oor-perf-card">
        <div className="oor-perf-header">
          <div>
            <h2 className="oor-section-title">Item Performance Analysis</h2>
            <p className="oor-perf-desc">Loading performance data...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Keep table fixed to highest profit margin first.
  const sorted = [...perfItems].sort((a, b) => {
    const marginA = Number.isFinite(Number(a.margin)) ? Number(a.margin) : -Infinity;
    const marginB = Number.isFinite(Number(b.margin)) ? Number(b.margin) : -Infinity;
    return marginB - marginA;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="oor-metric-card"
      style={{ marginBottom: '2.5rem' }}
    >
      <h2 className="oor-section-title" style={{ marginBottom: '1.5rem' }}>
        Top Profit-Making Items
      </h2>

      <div className={`oor-table-wrap ${sorted.length === 0 ? 'oor-table-wrap-empty' : ''}`}>
        {sorted.length === 0 ? (
          <NoDataPlaceholder
            type="profit"
            title="No profit data yet"
            subtitle="Your top earning items will rank here once sales are recorded."
            minHeight="200px"
          />
        ) : (
          <Table className="oor-table">
            <TableHeader>
              <TableRow>
                <TableHead>Item Name</TableHead>
                <TableHead className="oor-th-right">Qty</TableHead>
                <TableHead className="oor-th-right">Selling Price</TableHead>
                <TableHead className="oor-th-right">Your Cost</TableHead>
                <TableHead className="oor-th-right">Profit %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((item) => {
                return (
                  <TableRow key={item.item}>
                    <TableCell>
                      <span className="oor-td-item">{item.item}</span>
                    </TableCell>
                    <TableCell className="oor-td-right oor-td-num">{item.qty}</TableCell>
                    <TableCell className="oor-td-right oor-td-num">
                      {item.price !== null ? `₹${item.price}` : <span className="oor-text-muted">—</span>}
                    </TableCell>
                    <TableCell className="oor-td-right oor-td-num">
                      {item.cost !== null ? `₹${item.cost}` : <span className="oor-text-muted">—</span>}
                    </TableCell>
                    <TableCell className="oor-td-right">
                      {item.margin !== null ? (
                        <span
                          className={`oor-td-margin ${
                            item.margin > 40
                              ? 'oor-text-success'
                              : item.margin > 15
                              ? 'oor-text-warning'
                              : 'oor-text-destructive'
                          }`}
                        >
                          {item.margin}%
                        </span>
                      ) : (
                        <span className="oor-text-muted" style={{ fontSize: '0.875rem' }}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </motion.div>
  );
};

/* ================================================================
   MAIN PAGE - Now with dynamic data from API
   ================================================================ */
const OnlineOrdersReport = ({ goTo }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [itemsData, setItemsData] = useState(null);
  const [dailyBreakdown, setDailyBreakdown] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customDisplayLabel, setCustomDisplayLabel] = useState('');
  const [isRangeModeSelection, setIsRangeModeSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Order Detail Modal state
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [orderDetailData, setOrderDetailData] = useState([]);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetailError, setOrderDetailError] = useState(null);
  const [orderDetailSearchTerm, setOrderDetailSearchTerm] = useState('');

  // =================== PLATFORM UPLOAD STATE ===================
  const [showUploader, setShowUploader] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDate, setUploadDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [detectedPlatform, setDetectedPlatform] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [showUnmatchedModal, setShowUnmatchedModal] = useState(false);
  const [showPriceMismatchModal, setShowPriceMismatchModal] = useState(false);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [missingIngredients, setMissingIngredients] = useState([]);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Which report load is the current one.
   *
   * Switching period or platform chip starts a new load while the previous one is still in
   * flight. Without a ticket, a slow response for the chip the user just left can land last
   * and repaint the report with the wrong platform's figures. Only the holder of the newest
   * ticket may write state or clear the spinner.
   */
  const requestSeqRef = useRef(0);

  // Swiggy Order Report (multi-date, order-level) review state
  const [orderReport, setOrderReport] = useState(null);       // { orders, dateSummary, summary, matchSummary }
  const [showOrderReportModal, setShowOrderReportModal] = useState(false);
  const [orSubmitting, setOrSubmitting] = useState(false);
  const [orResults, setOrResults] = useState(null);            // per-date import results
  const [orExpandedDates, setOrExpandedDates] = useState({});  // { 'YYYY-MM-DD': bool }
  const [orMenuList, setOrMenuList] = useState([]);            // for manual completion autocomplete
  const [orManualAdds, setOrManualAdds] = useState({});        // { orderIndex: [{ item_name, mapped_menu_item_id, db_price, quantity, price }] }

  // Revert state (localhost/admin only)
  const isLocalhost = (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.')
  );
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertList, setRevertList] = useState([]);
  const [revertLoading, setRevertLoading] = useState(false);
  const [revertTargetId, setRevertTargetId] = useState(null);
  const [revertingId, setRevertingId] = useState(null);
  const [revertSuccess, setRevertSuccess] = useState(null);
  const [revertError, setRevertError] = useState(null);

  // Platform chips filter
  const [selectedPlatformChip, setSelectedPlatformChip] = useState('all');
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [showExportPicker, setShowExportPicker] = useState(false);
  const [onlineSharing, setOnlineSharing] = useState(false);

  const periodMeta = getDateRangeForPeriod(selectedPeriod, customStartDate, customEndDate);
  const activePeriodLabel = selectedPeriod === 'custom' && customDisplayLabel
    ? customDisplayLabel
    : periodMeta.label;
  const periodTriggerLabel = activePeriodLabel || 'This Week';

  // Share the channel-separated editorial PDF (Web Share API, download fallback),
  // mirroring the Sales Report's Share button. Always all channels.
  const handleShareReport = useCallback(async () => {
    if (onlineSharing) return;
    setOnlineSharing(true);
    try {
      const input = await fetchOnlineReportInput({
        start: periodMeta.startDate, end: periodMeta.endDate,
        label: activePeriodLabel || periodTriggerLabel,
      });
      const stamp = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      const fileName = `Online_Report_${stamp}.pdf`;
      const blob = await buildEditorialOnlinePdfBlob(input, fileName);
      const pdfFile = new File([blob], fileName, { type: 'application/pdf' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        await navigator.share({ title: 'Online Report', text: 'Online sales report from Invexis', files: [pdfFile] });
        return;
      }
      // Fallback: download the PDF when direct file share is unsupported.
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = fileName; link.rel = 'noopener noreferrer';
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e && e.name === 'AbortError') return; // user dismissed the share sheet
      console.error('Online report share failed:', e);
      alert('Unable to share the report. Please try again.');
    } finally {
      setOnlineSharing(false);
    }
  }, [onlineSharing, periodMeta, activePeriodLabel, periodTriggerLabel]);

  const filteredOrderDetailData = useMemo(() => {
    if (!orderDetailSearchTerm.trim()) return orderDetailData;

    const searchLower = orderDetailSearchTerm.trim().toLowerCase();
    return orderDetailData.filter((order) => {
      const orderId = String(order.online_order_id ?? '').toLowerCase();
      const orderIdMatch = orderId.includes(searchLower);
      const itemMatch = Array.isArray(order.items) && order.items.some((item) => (
        String(item.item_name ?? '').toLowerCase().includes(searchLower)
      ));

      return orderIdMatch || itemMatch;
    });
  }, [orderDetailData, orderDetailSearchTerm]);

  const closeOrderDetailModal = () => {
    setShowOrderDetailModal(false);
    setOrderDetailSearchTerm('');
  };

  // Fetch platform list on mount
  useEffect(() => {
    http.get('/online-platforms')
      .then(res => {
        const list = res.platforms || res.data?.platforms || [];
        setAvailablePlatforms(list);
      })
      .catch(() => {
        setAvailablePlatforms([
          { platform_id: 'd1', name: 'Zomato', is_default: true },
          { platform_id: 'd2', name: 'Swiggy', is_default: true },
          { platform_id: 'd3', name: 'Others', is_default: true },
        ]);
      });
  }, []);

  useEffect(() => {
    if (selectedPeriod === 'custom' && !periodMeta.isValid) {
      return;
    }

    const fetchData = async () => {
      const mySeq = ++requestSeqRef.current;
      try {
        setIsLoading(true);
        setError(null);

        // Fetch all online orders data in parallel.
        //
        // The `_t` cache-buster that used to be added here is gone. It made every URL unique,
        // which defeated caching without buying any freshness: the server-side report cache is
        // wiped by invalidateBusinessReportCaches() the moment an online order is uploaded,
        // submitted or reverted, so a repeat load is either genuinely current or genuinely new.
        const searchParams = new URLSearchParams();
        if (periodMeta.startDate && periodMeta.endDate) {
          searchParams.set('startDate', periodMeta.startDate);
          searchParams.set('endDate', periodMeta.endDate);
        }
        // Add platform filter if selected
        if (selectedPlatformChip && selectedPlatformChip !== 'all') {
          searchParams.set('platform', selectedPlatformChip);
        }
        const qs = searchParams.toString();
        const queryString = qs ? `?${qs}` : '';

        // The item list and the daily breakdown carry their own fallbacks: they feed separate
        // sections, and one of them failing should not blank the dashboard cards that loaded
        // fine. The dashboard itself has no fallback on purpose — it is the headline, and
        // zeroed cards presented as real numbers are worse than an error.
        const [dashboardRes, itemsRes, dailyRes] = await Promise.all([
          http.get(`/online-orders/dashboard${queryString}`),
          http.get(`/online-orders/item-wise-report${queryString}`).catch(() => ({ data: { items: [] } })),
          http.get(`/online-orders/daily-breakdown${queryString}`).catch(() => ({ data: { daily: [] } }))
        ]);

        // A newer period/platform was picked while these were in flight; those results are
        // the ones that belong on screen.
        if (mySeq !== requestSeqRef.current) return;

        // Backend returns { success: true, data: {...} }
        // httpClient already returns parsed JSON response
        const dashboardPayload = dashboardRes?.data || dashboardRes || null;
        const itemsPayload = itemsRes?.data || itemsRes || {};
        const dailyPayload = dailyRes?.data || dailyRes || {};

        setDashboardData(dashboardPayload);
        
        // Map backend field names to frontend expected names
        const rawItems = Array.isArray(itemsPayload.items) ? itemsPayload.items : [];
        const mappedItems = rawItems.map(item => {
          const cogsPerUnit = Number(item.cogsPerUnit || 0);
          const hasRecipe = item.hasRecipe || false;
          // Backend flags items oversold into negative stock; their GP is not shown ("-").
          const grossProfitUnavailable = Boolean(item.grossProfitUnavailable);

          // ── Single revenue base (reconciliation rule) ───────────────────
          // Exactly ONE revenue number per item so every derived figure adds up:
          //   • Total Sales (net, after merchant discount) is THE base.
          //   • unit price      = Total Sales ÷ qty   (never a separate menu avg)
          //   • Gross Profit    = Total Sales − Total COGS
          //   • margin %        = GP ÷ Total Sales
          // This guarantees unit×qty === Total Sales and GP < Total Sales.
          //
          // Wastage is NOT netted in here. It used to be, and that put two different
          // "Gross Profit" figures on one screen: the dashboard card computes
          // finalSales − COGS, so for July 2026 the card and this table were
          // ₹48,979.56 apart — the exact online wastage for the period — under the same
          // label. Revenue − COGS is also what the Sales EOM report and
          // /reports online summary mean by gross profit, so this is the definition that
          // reconciles across the app. Wastage remains reported on its own, per item and
          // in the wastage card, rather than being buried inside a profit number.
          const qtySold = Number(item.quantitySold || 0);
          const grossMenuTotal = Number(item.totalSales || 0);      // Σ menu price × qty (pre-discount)
          const totalDiscount = Number(item.totalDiscount || 0);
          // finalSales from backend is the after-discount actual revenue.
          const netSales = Number(
            item.finalSales != null ? item.finalSales : (grossMenuTotal - totalDiscount)
          );
          const unitPrice = qtySold > 0 ? netSales / qtySold : 0;   // reconciled unit price
          const totalCOGS = Number(item.totalCOGS || 0);
          const wastageCost = Number(item.wastageAmount || 0);
          const grossProfit = netSales - totalCOGS;                 // GP = Sales − COGS
          const cogsKnown = hasRecipe && cogsPerUnit > 0 && !grossProfitUnavailable;
          const margin = (!cogsKnown || netSales <= 0)
            ? null
            : Number((grossProfit / netSales * 100).toFixed(1));

          return {
            menuItemId: item.menuItemId || item.menu_item_id || null,
            name: item.itemName,
            imageUrl: resolveItemImageUrl(
              item.imageUrl || item.image_url || item.image || item.itemImage || item.item_image_url || ''
            ),
            channel: item.channel || item.platform || item.platformName || null,
            itemCategory: item.itemCategory || item.category || null,
            price: unitPrice,                       // reconciled net unit price
            menuUnitPrice: qtySold > 0 ? grossMenuTotal / qtySold : Number(item.avgUnitPrice || 0),
            grossMenuTotal: grossMenuTotal,         // pre-discount menu value (for the ladder)
            qtySold: qtySold,
            totalSale: netSales,                    // headline = reconciled net revenue
            totalCOGS: totalCOGS,
            finalSales: netSales,
            grossProfit: grossProfit,
            margin: margin,
            cogsKnown: cogsKnown,
            grossProfitUnavailable: grossProfitUnavailable,
            wastagePlates: Number(item.wastageQuantity || 0),
            wastageCost: wastageCost,
            wastageCount: Number(item.wastageCount || 0),
            orderCount: Number(item.orderCount || 0),
            totalDiscount: totalDiscount,
            cogsPerUnit: cogsPerUnit,
            hasRecipe: hasRecipe,
            ingredientsUsed: item.ingredientsUsed || [],
            recipeCost: Number(item.recipeCost || 0),
            complimentaryCost: Number(item.complimentaryCost || 0),
            preparationCost: Number(item.preparationCost || 0),
            complimentaryIngredients: item.complimentaryIngredients || [],
            preparationAdjustments: item.preparationAdjustments || []
          };
        });
        // Under "All" a dish sold on two platforms is one row, not two.
        setItemsData(selectedPlatformChip === 'all' ? mergeItemsAcrossPlatforms(mappedItems) : mappedItems);
        
        setDailyBreakdown(Array.isArray(dailyPayload.daily) ? dailyPayload.daily : []);
      } catch (err) {
        if (mySeq !== requestSeqRef.current) return;
        console.error('Error fetching online orders data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        // Only the newest load owns the spinner — see requestSeqRef.
        if (mySeq === requestSeqRef.current) setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod, periodMeta.isValid, periodMeta.startDate, periodMeta.endDate, selectedPlatformChip]);

  // =================== PLATFORM UPLOAD HANDLERS ===================
  const handleUploadParse = useCallback(async () => {
    if (!uploadFile) return;
    setIsUploading(true);
    setUploadError(null);
    setUploadResult(null);
    setReviewItems([]);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const resp = await http.post('/online-sales/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = resp.data || resp;
      if (data.success) {
        setDetectedPlatform(data.platform);
        if (data.is_order_report) {
          // Multi-date Swiggy Order Report → open the dedicated review modal
          setOrderReport(data);
          setOrResults(null);
          setOrManualAdds({});
          setOrExpandedDates({});
          setShowOrderReportModal(true);
          // Load menu for manual completion of truncated orders (best-effort)
          try {
            const menuResp = await MenuService.getMenuItems('en', true);
            if (menuResp?.success && Array.isArray(menuResp.data)) setOrMenuList(menuResp.data);
          } catch (_) { /* non-fatal */ }
        } else {
          setReviewItems(data.items || []);
          setUploadResult(data);
        }
      } else {
        setUploadError(data.error || 'Upload failed');
      }
    } catch (err) {
      setUploadError(err.response?.data?.error || err.data?.error || err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [uploadFile]);

  // Build the per-date submit payload from reviewed orders (+ any manual completions)
  const buildOrderReportDates = useCallback(() => {
    if (!orderReport) return [];
    const importedDates = new Set(
      (orderReport.dateSummary || []).filter(d => d.already_imported).map(d => d.order_date)
    );
    const byDate = new Map();

    orderReport.orders.forEach((o, idx) => {
      if (o.is_cancelled || !o.order_date || importedDates.has(o.order_date)) return;
      const collected = [];
      o.items.forEach(it => {
        if (it.is_matched && it.mapped_menu_item_id) {
          collected.push({
            menu_item_id: it.mapped_menu_item_id,
            item_name: it.matched_name || it.item_name,
            quantity: it.quantity,
            unit_price: it.true_selling_price || 0,
            amount: (it.true_selling_price || 0) * it.quantity,
            platform_original_price: it.platform_original_price || 0,
            platform_discount: it.platform_discount || 0,
            platform_commission: 0,
            true_selling_price: it.true_selling_price || 0,
          });
        }
      });
      (orManualAdds[idx] || []).forEach(m => {
        if (!m.mapped_menu_item_id || !m.quantity || !m.price) return;
        collected.push({
          menu_item_id: m.mapped_menu_item_id,
          item_name: m.item_name,
          quantity: Number(m.quantity),
          unit_price: Number(m.price),
          amount: Number(m.price) * Number(m.quantity),
          platform_original_price: Number(m.price) * Number(m.quantity),
          platform_discount: 0,
          platform_commission: 0,
          true_selling_price: Number(m.price),
        });
      });
      if (!collected.length) return;
      if (!byDate.has(o.order_date)) byDate.set(o.order_date, []);
      byDate.get(o.order_date).push(...collected);
    });

    // Lines not on the menu go in as Not in Menu lines of their date, money kept
    // (spec 2026-10-26-tsr-add-as-not-in-menu) — a date with only those is still imported.
    const notInMenuByDate = orderReportUnmappedByDate(orderReport.orders, { importedDates });
    for (const d of notInMenuByDate.keys()) if (!byDate.has(d)) byDate.set(d, []);

    // Aggregate each date's items by menu_item_id
    const dates = [];
    for (const [order_date, items] of byDate) {
      const agg = new Map();
      items.forEach(it => {
        if (!agg.has(it.menu_item_id)) { agg.set(it.menu_item_id, { ...it }); }
        else {
          const e = agg.get(it.menu_item_id);
          e.quantity += it.quantity;
          e.amount += it.amount;
          e.platform_original_price += it.platform_original_price;
          e.platform_discount += it.platform_discount;
        }
      });
      dates.push({ order_date, salesItems: [...agg.values()], unmappedItems: notInMenuByDate.get(order_date) || [] });
    }
    return dates.sort((a, b) => (a.order_date < b.order_date ? -1 : 1));
  }, [orderReport, orManualAdds]);

  const newDatesCount = useMemo(
    () => (orderReport?.dateSummary || []).filter(d => !d.already_imported).length,
    [orderReport]
  );

  const handleSubmitOrderReport = useCallback(async () => {
    const dates = buildOrderReportDates();
    if (!dates.length) {
      setUploadError('No new dates to import (all already imported or nothing to import).');
      return;
    }
    setOrSubmitting(true);
    setUploadError(null);
    try {
      const resp = await http.post('/online-sales/submit-order-report', {
        dates,
        platformSource: 'swiggy',
        allowNegativeStock,
      });
      const data = resp.data || resp;
      if (data.success) {
        setOrResults(data);
        setSubmitSuccess(data.message || 'Import complete');
        setSelectedPeriod(prev => prev); // refresh dashboard
      } else {
        setUploadError(data.error || 'Import failed');
      }
    } catch (err) {
      setUploadError(err.data?.error || err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setOrSubmitting(false);
    }
  }, [buildOrderReportDates, allowNegativeStock]);

  const addManualItem = useCallback((orderIdx, entry) => {
    // Resolve the picked menu name → id/price
    const match = orMenuList.find(m => (m.name || '').toLowerCase() === (entry.item_name || '').toLowerCase());
    if (!match) { setUploadError(`"${entry.item_name}" is not in the menu — pick a listed item.`); return; }
    setOrManualAdds(prev => ({
      ...prev,
      [orderIdx]: [...(prev[orderIdx] || []), {
        item_name: match.name,
        mapped_menu_item_id: match.menu_item_id,
        db_price: Number(match.price) || 0,
        quantity: Number(entry.quantity) || 1,
        price: Number(entry.price) || Number(match.price) || 0,
      }]
    }));
  }, [orMenuList]);

  const removeManualItem = useCallback((orderIdx, i) => {
    setOrManualAdds(prev => ({
      ...prev,
      [orderIdx]: (prev[orderIdx] || []).filter((_, idx) => idx !== i)
    }));
  }, []);

  const closeOrderReportModal = useCallback(() => {
    setShowOrderReportModal(false);
    setOrderReport(null);
    setOrResults(null);
    setOrManualAdds({});
    setUploadFile(null);
  }, []);

  const handleSubmitOnlineSales = useCallback(async () => {
    if (!reviewItems.length) return;
    const matchedItems = reviewItems.filter(i => i.is_matched && i.mapped_menu_item_id);
    // Lines not on the menu are recorded as Not in Menu, money kept — never dropped.
    const unmappedItems = onlineUnmappedItems(reviewItems);
    if (matchedItems.length === 0 && unmappedItems.length === 0) {
      setUploadError('No items to submit.');
      return;
    }
    setIsSubmitting(true);
    setUploadError(null);
    try {
      const salesItems = matchedItems.map(item => ({
        menu_item_id: item.mapped_menu_item_id,
        item_name: item.matched_name || item.item_name,
        quantity: item.total_quantity || 1,
        unit_price: item.true_selling_price || item.unit_price,
        amount: item.total_amount || (item.total_quantity * item.unit_price),
        platform_original_price: item.platform_original_price || 0,
        platform_discount: item.platform_discount || 0,
        platform_commission: item.platform_commission || 0,
        true_selling_price: item.true_selling_price || item.unit_price,
      }));
      const totalAmount = salesItems.reduce((s, i) => s + (i.amount || 0), 0);
      const discountAmount = salesItems.reduce((s, i) => s + (i.platform_discount || 0), 0);
      const commissionTotal = salesItems.reduce((s, i) => s + (i.platform_commission || 0), 0);

      const resp = await http.post('/online-sales/submit', {
        salesItems,
        reportDate: uploadDate,
        totalAmount,
        discountAmount,
        platformSource: detectedPlatform || 'generic',
        commissionTotal,
        allowNegativeStock,
        unmappedItems
      });
      const data = resp.data || resp;
      if (data.success) {
        setSubmitSuccess(data.message || 'Submitted successfully!');
        setShowUploader(false);
        setUploadFile(null);
        setUploadResult(null);
        setReviewItems([]);
        // Refresh dashboard
        setSelectedPeriod(prev => prev);
      } else {
        if (data.code === 'PREPARATION_SHORTAGE') {
          setMissingIngredients(data.missingIngredients || []);
          setShowMissingModal(true);
        } else {
          setUploadError(data.error || 'Submission failed');
        }
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.code === 'PREPARATION_SHORTAGE') {
        setMissingIngredients(errData.missingIngredients || []);
        setShowMissingModal(true);
      } else {
        setUploadError(errData?.error || err.message || 'Submission failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [reviewItems, uploadDate, detectedPlatform, allowNegativeStock]);

  const handleOpenRevert = useCallback(async () => {
    setShowRevertModal(true);
    setRevertLoading(true);
    setRevertSuccess(null);
    try {
      const resp = await http.get('/online-sales/recent-list');
      const data = resp.data || resp;
      setRevertList(data.orders || []);
    } catch (err) {
      console.error('Failed to load revert list:', err);
    } finally {
      setRevertLoading(false);
    }
  }, []);

  const handleRevertOrder = useCallback(async (orderId) => {
    setRevertingId(orderId);
    setRevertError(null);
    try {
      const resp = await postUndo(`/online-sales/revert/${orderId}`, {});
      // httpClient returns JSON body directly; resp.data is the nested sub-field,
      // NOT the full response. Read resp directly since it has the success flag.
      const result = (resp && resp.success !== undefined) ? resp : (resp.data || resp);
      if (result.success) {
        setRevertSuccess(result.message);
        setRevertTargetId(null);
        setRevertList(prev => prev.filter(o => o.online_order_id !== orderId));
        setSelectedPeriod(prev => prev);
      } else {
        setRevertError(result.error || 'Revert failed');
      }
    } catch (err) {
      // httpClient throws with err.data (not err.response?.data)
      const errData = err.data || err.response?.data;
      setRevertError(errData?.error || err.message || 'Revert failed');
    } finally {
      setRevertingId(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) setUploadFile(file);
  }, []);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);

    if (period !== 'custom') {
      setCustomStartDate('');
      setCustomEndDate('');
      setCustomDisplayLabel('');
      setIsRangeModeSelection(false);
    }
  };

  const handleCalendarRangeSelect = ({ startDate, endDate, label, isRangeMode }) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return;
    }

    const normalizedStart = start <= end ? start : end;
    const normalizedEnd = end >= start ? end : start;
    const isMultiDayRange = formatDateParam(normalizedStart) !== formatDateParam(normalizedEnd);

    setCustomStartDate(formatDateParam(normalizedStart));
    setCustomEndDate(formatDateParam(normalizedEnd));
    setCustomDisplayLabel(label || formatRangeDisplayDMY(normalizedStart, normalizedEnd));
    setIsRangeModeSelection(Boolean(isRangeMode && isMultiDayRange));
    setSelectedPeriod('custom');
  };

  // Handler for Total Sales card click
  const handleTotalSalesClick = async () => {
    try {
      setOrderDetailLoading(true);
      setOrderDetailError(null);
      setOrderDetailSearchTerm('');
      setShowOrderDetailModal(true);

      // Determine which date/range to use
      const params = new URLSearchParams();
      if (periodMeta.startDate && periodMeta.endDate) {
        params.set('start_date', periodMeta.startDate);
        params.set('end_date', periodMeta.endDate);
      } else {
        // Default to today
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        params.set('date', today);
      }
      // Keep the drill-down consistent with the active chip
      if (selectedPlatformChip && selectedPlatformChip !== 'all') {
        params.set('platform', selectedPlatformChip);
      }

      const detailEndpoint = `/online-orders/detail?${params.toString()}`;
      console.log('📊 Fetching online order detail with endpoint:', detailEndpoint);

      // Fetch detail data
      const response = await http.get(detailEndpoint);

      if (response.success) {
        setOrderDetailData(response.orders || []);
        console.log('✅ Loaded', response.orders?.length, 'online orders');
      } else {
        throw new Error(response.error || 'Failed to load order details');
      }
    } catch (error) {
      console.error('Failed to fetch order detail:', error);
      setOrderDetailError(error.message || 'Failed to load order details');
    } finally {
      setOrderDetailLoading(false);
    }
  };

  if (error) {
    return (
      <div className="oor-root">
        <div className="oor-container">
          <OnlineReportTopHeader goTo={goTo} />
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: 'var(--oor-destructive)', marginBottom: '1rem' }}>
              {error}
            </p>
            <button 
              onClick={() => window.location.reload()} 
              className="oor-view-btn"
              style={{ padding: '0.5rem 1rem' }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="oor-root">
      <div className="oor-container">
        {isLoading && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            flexDirection: 'column',
            gap: '20px',
            width: '100%',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 999,
            background: '#ffffff'
          }}>
            <div style={{ position: 'relative', width: '95px', height: '95px' }}>
              <img
                src="/invexis-logo.png"
                alt="Invexis"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  padding: '0px',
                  pointerEvents: 'none',
                  objectFit: 'contain',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  zIndex: 1
                }}
              />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '70px',
                height: '70px',
                border: '6px solid #000000',
                borderTop: '6px solid #f9c806',
                borderRight: '6px solid #f9c806',
                borderRadius: '50%',
                animation: 'salesReportSpin 1s linear infinite',
                zIndex: 16
              }}></div>
            </div>
            <p style={{
              color: '#666',
              fontSize: '16px',
              textAlign: 'center',
              margin: 0
            }}>Loading sales data...</p>
            <style>{`
              @keyframes salesReportSpin {
                0% { transform: translate(-50%, -50%) rotate(0deg); }
                100% { transform: translate(-50%, -50%) rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {!isLoading && (
          <>
            <OnlineReportTopHeader goTo={goTo} />

            {/* ========== PLATFORM FILTER CHIPS ========== */}
            <div style={{
              display: 'flex', overflowX: 'auto', gap: '8px',
              padding: '0 16px 2px',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
            }}>
              {/* All chip */}
              {[{ platform_id: 'all', name: 'All' }, ...availablePlatforms].map(p => {
                // chipKey must match what's sent as ?platform= and what's stored in
                // platform_source (both lowercase), or the active chip never highlights.
                const chipKey = p.name.toLowerCase();
                const isActive = selectedPlatformChip === chipKey;
                const colorMap = {
                  Zomato: { active: '#e23744', light: '#fff4f4', text: '#e23744' },
                  Swiggy: { active: '#fc8019', light: '#fff5ee', text: '#fc8019' },
                  Others: { active: '#374151', light: '#f3f4f6', text: '#374151' },
                  All:    { active: '#111827', light: '#f3f4f6', text: '#111827' },
                };
                const cm = colorMap[p.name] || { active: '#111827', light: '#f3f4f6', text: '#111827' };
                return (
                  <button
                    key={p.platform_id || p.name}
                    type="button"
                    onClick={() => setSelectedPlatformChip(chipKey)}
                    style={{
                      flexShrink: 0,
                      padding: '7px 16px',
                      borderRadius: '50px',
                      border: `1.5px solid ${isActive ? cm.active : '#e5e7eb'}`,
                      background: isActive ? cm.active : '#fff',
                      color: isActive ? '#fff' : cm.text,
                      fontWeight: '600', fontSize: '0.82rem',
                      cursor: 'pointer', transition: 'all 0.15s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
            {/* ========== END PLATFORM CHIPS ========== */}

            <OnlinePeriodFilter
              selectedPeriod={selectedPeriod}
              triggerLabel={periodTriggerLabel}
              onPeriodChange={handlePeriodChange}
              customOptionLabel={customDisplayLabel}
              onCalendarRangeSelect={handleCalendarRangeSelect}
            />

            <PulseHeader
              dashboardData={dashboardData}
              selectedPeriod={selectedPeriod}
              bepBasisDate={periodMeta.endDate || periodMeta.startDate || ''}
              onTotalSalesClick={handleTotalSalesClick}
            />
            <SalesBarChart
              dailyBreakdown={dailyBreakdown}
              itemsData={itemsData}
              isLoading={isLoading}
              selectedPeriod={selectedPeriod}
              showDateWiseBreakdownTable={isRangeModeSelection}
              periodLabel={activePeriodLabel}
              periodStartDate={periodMeta.startDate}
              periodEndDate={periodMeta.endDate}
            />
            <ItemSalesReport itemsData={itemsData} isLoading={isLoading} />
            {/* <ItemPerformanceTable itemsData={itemsData} isLoading={isLoading} /> */}

            {/* ===== EXPORT / SHARE FOOTER (same pattern as Sales & Stock reports) ===== */}
            <div style={{ display: 'flex', gap: '1rem', padding: '1rem 0 1.5rem' }}>
              <button
                type="button"
                onClick={() => setShowExportPicker(true)}
                style={{
                  flex: 1, padding: '0.85rem', borderRadius: 8, border: 'none',
                  fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  background: '#e9ecef', color: '#000',
                }}
              >
                <FileText size={18} /> Export Report
              </button>
              <button
                type="button"
                onClick={handleShareReport}
                disabled={onlineSharing}
                style={{
                  flex: 1, padding: '0.85rem', borderRadius: 8, border: 'none',
                  fontSize: '0.9rem', fontWeight: 600, cursor: onlineSharing ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  background: '#000', color: '#fff', opacity: onlineSharing ? 0.7 : 1,
                }}
              >
                <Share2 size={18} /> {onlineSharing ? 'Preparing…' : 'Share'}
              </button>
            </div>
          </>
        )}

        {/* Online Export Picker Modal */}
        <OnlineExportReportModal
          isOpen={showExportPicker}
          onClose={() => setShowExportPicker(false)}
          periodStart={periodMeta.startDate}
          periodEnd={periodMeta.endDate}
          periodLabel={activePeriodLabel || periodTriggerLabel}
        />

        {/* Order Detail Breakdown Modal */}
        {showOrderDetailModal && (
          <div className="modal-overlay-order-detail" onClick={closeOrderDetailModal}>
            <div className="modal-content-order-detail" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-order-detail">
                <div>
                  <h2>Sales Detail Breakdown</h2>
                  <p className="modal-subtitle-order-detail">Complete order details with tax breakdown</p>
                </div>
                <button 
                  onClick={closeOrderDetailModal}
                  className="close-btn-order-detail"
                  title="Close"
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body-order-detail">
                {orderDetailLoading ? (
                  <div className="loading-order-detail">
                    <div className="spinner-order-detail"></div>
                    <p>Loading order details...</p>
                  </div>
                ) : orderDetailError ? (
                  <div className="error-order-detail">
                    <p>❌ {orderDetailError}</p>
                    <button onClick={handleTotalSalesClick} className="retry-btn-order-detail">
                      Retry
                    </button>
                  </div>
                ) : orderDetailData.length === 0 ? (
                  <NoDataPlaceholder
                    type="sales"
                    title="No order data found"
                    subtitle="No online orders found for the selected period."
                    minHeight="200px"
                  />
                ) : (
                  <>
                    <div className="order-detail-search-row">
                      <StandardSearch
                        value={orderDetailSearchTerm}
                        onChange={setOrderDetailSearchTerm}
                        placeholder="Search by order ID or item"
                        showClearButton={true}
                      />
                    </div>

                    {filteredOrderDetailData.length === 0 ? (
                      <div className="empty-order-detail order-detail-empty-filtered">
                        <p>No matching orders found.</p>
                      </div>
                    ) : (
                      <div className="detail-list-order-detail">
                        {filteredOrderDetailData.map((order, orderIndex) => {
                          const orderItems = Array.isArray(order.items) ? order.items : [];
                          return (
                          <div key={orderIndex} className="order-group-detail">
                            <div className="order-header-detail">
                              <span className="order-id-detail">
                                {order.order_date ? formatDateDisplayDMY(order.order_date) : `Order #${order.online_order_id}`}
                              </span>
                              <span className="order-time-detail">{order.time}</span>
                            </div>
                            <Table className="detail-table-order-detail">
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="sno-col-order-detail">S.No</TableHead>
                                  <TableHead>Item</TableHead>
                                  <TableHead className="text-right">Qty</TableHead>
                                  <TableHead className="text-right">Price</TableHead>
                                  <TableHead className="text-right">Disc. Price</TableHead>
                                  <TableHead className="text-right">Amount</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {orderItems.map((item, itemIndex) => {
                                  const originalPrice = item.original_menu_price || 0; // Menu price from MenuItems table
                                  const soldPrice = item.unit_price || 0; // Actual sold price
                                  const hasDiscount = originalPrice > soldPrice;
                                  const soldLineAmount = Number(
                                    item.final_line_amount
                                    ?? (soldPrice * (item.quantity_sold || 0))
                                    ?? item.line_item_amount
                                    ?? 0
                                  );

                                  return (
                                    <TableRow key={itemIndex} className="detail-row-order-detail">
                                      <TableCell className="sno-col-order-detail">{itemIndex + 1}</TableCell>
                                      <TableCell className="item-name-order-detail">{toTitleCase(item.item_name)}</TableCell>
                                      <TableCell className="qty-col-order-detail text-right">
                                        {item.quantity_sold}
                                      </TableCell>
                                      <TableCell className={`price-col-order-detail text-right ${hasDiscount ? 'has-discount-strikethrough' : ''}`}>
                                        ₹{originalPrice.toFixed(2)}
                                      </TableCell>
                                      <TableCell className={`disc-price-col-order-detail text-right ${hasDiscount ? 'has-discount' : ''}`}>
                                        ₹{soldPrice.toFixed(2)}
                                      </TableCell>
                                      <TableCell className="amount-col-order-detail text-right">
                                        ₹{soldLineAmount.toFixed(2)}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                            {/* Subtotal − Discount must equal Total, so all three are read on
                                the realised basis. `total_amount` is the MENU price of the
                                order, not the platform subtotal it was discounted from:
                                order 447 carries total 3154, discount 30, final 3363, which
                                printed as "3154 − 30 = 3363". The subtotal is therefore
                                rebuilt from the total the customer actually paid. */}
                            <div className="order-summary-detail">
                              <div className="summary-row-detail">
                                <span>Subtotal ({orderItems.length} items)</span>
                                <strong>₹{(Number(order.final_amount || 0) + Number(order.discount_amount || 0)).toFixed(2)}</strong>
                              </div>
                              {order.discount_amount > 0 && (
                                <div className="summary-row-detail discount-row-detail">
                                  <span>Discount</span>
                                  <strong>-₹{order.discount_amount.toFixed(2)}</strong>
                                </div>
                              )}
                              <div className="summary-row-detail total-row-detail">
                                <span>Total</span>
                                <strong>₹{Number(order.final_amount || 0).toFixed(2)}</strong>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                        <div className="grand-total-detail">
                          <span>
                            Total ({filteredOrderDetailData.length} orders, {filteredOrderDetailData.reduce((sum, order) => sum + (Array.isArray(order.items) ? order.items.length : 0), 0)} items)
                          </span>
                          {/* Realised, so this modal's grand total equals the Total Sales
                              card that opens it. Summing total_amount made the card read
                              ₹4,50,022.12 and its own detail view ₹4,02,916.26. */}
                          <strong>
                            ₹{filteredOrderDetailData.reduce((sum, order) => sum + Number(order.final_amount || 0), 0).toFixed(2)}
                          </strong>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Platform report upload + revert moved to the Total Sales Report (TSR) screen
          under Online Order Mode. Reports is analytics-only. */}

      {/* Submit success toast */}
      {submitSuccess && (
        <div className="oor-toast oor-toast-success" onClick={() => setSubmitSuccess(null)}>
          <Check size={18} /> {submitSuccess}
        </div>
      )}

      {/* =================== UPLOAD ZONE =================== */}
      {showUploader && (
        <div className="oor-uploader-panel">
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 700 }}>
            <FileSpreadsheet size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Upload Swiggy / Zomato Report
          </h3>

          <div
            className={`oor-drop-zone ${dragging ? 'dragging' : ''} ${uploadFile ? 'has-file' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx,.csv"
              style={{ display: 'none' }}
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
            {uploadFile ? (
              <div style={{ textAlign: 'center' }}>
                <FileSpreadsheet size={32} color="#10b981" />
                <div style={{ marginTop: 8, fontWeight: 600 }}>{uploadFile.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{(uploadFile.size / 1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#6b7280' }}>
                <Upload size={32} />
                <div style={{ marginTop: 8 }}>Drag & drop Excel/CSV file or click to browse</div>
                <div style={{ fontSize: 12 }}>.xls, .xlsx, .csv supported</div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 14, fontWeight: 600 }}>Report Date:</label>
            <input
              type="date"
              value={uploadDate}
              onChange={(e) => setUploadDate(e.target.value)}
              className="oor-date-input"
            />
            <button
              className="oor-parse-btn"
              onClick={handleUploadParse}
              disabled={!uploadFile || isUploading}
            >
              {isUploading ? 'Parsing...' : 'Upload & Parse'}
            </button>
          </div>

          {uploadError && (
            <div className="oor-toast oor-toast-error" style={{ marginTop: 12 }}>
              <XCircle size={16} /> {uploadError}
            </div>
          )}

          {/* Platform badge */}
          {detectedPlatform && uploadResult && (
            <div className="oor-platform-badge-row" style={{ marginTop: 12 }}>
              <span className={`oor-platform-badge ${detectedPlatform}`}>
                {detectedPlatform === 'swiggy' ? '🟠 Swiggy Detected' :
                 detectedPlatform === 'zomato' ? '🔴 Zomato Detected' :
                 '📊 Generic Format'}
              </span>
              <span style={{ fontSize: 13, color: '#6b7280' }}>
                {uploadResult.matchSummary?.matched || 0} matched · {uploadResult.matchSummary?.unmatched || 0} unmatched
              </span>
            </div>
          )}

          {/* =================== REVIEW TABLE =================== */}
          {reviewItems.length > 0 && (
            <div className="oor-review-section">
              <h4 style={{ margin: '20px 0 12px', fontSize: 16, fontWeight: 700 }}>Review Items</h4>
              <div className="oor-review-table-wrap">
                <table className="oor-review-table">
                  <thead>
                    <tr>
                      <th>Item Name</th>
                      <th>Qty</th>
                      <th>Platform Price</th>
                      <th>Menu Price</th>
                      <th>Discount</th>
                      <th>Commission</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewItems.map((item, idx) => {
                      const isUnmatched = !item.is_matched;
                      const hasPriceMismatch = item.is_matched && item.db_price && item.true_selling_price &&
                        Math.abs(item.true_selling_price - item.db_price) / item.db_price > 0.05;
                      return (
                        <tr key={idx} className={isUnmatched ? 'oor-row-unmatched' : hasPriceMismatch ? 'oor-row-mismatch' : 'oor-row-matched'}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{item.item_name}</div>
                            {item.is_matched && <div style={{ fontSize: 11, color: '#6b7280' }}>→ {item.matched_name}</div>}
                          </td>
                          <td>{item.total_quantity}</td>
                          <td>₹{(item.true_selling_price || 0).toFixed(2)}</td>
                          <td>{item.db_price ? `₹${item.db_price.toFixed(2)}` : '—'}</td>
                          <td>₹{(item.platform_discount || 0).toFixed(2)}</td>
                          <td>₹{(item.platform_commission || 0).toFixed(2)}</td>
                          <td>
                            {isUnmatched ? (
                              <span className="oor-status-badge unmatched" title="Recorded as Not in Menu with its money; map it later in Not in Menu">Will go to Not in Menu</span>
                            ) : hasPriceMismatch ? (
                              <span className="oor-status-badge mismatch">Price Diff</span>
                            ) : (
                              <span className="oor-status-badge matched">✓ Matched</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary bar */}
              <div className="oor-submit-bar">
                <div className="oor-submit-summary">
                  <span>Total: ₹{reviewItems.reduce((s, i) => s + (i.total_amount || 0), 0).toFixed(2)}</span>
                  <span>Discount: ₹{reviewItems.reduce((s, i) => s + (i.platform_discount || 0), 0).toFixed(2)}</span>
                  <span>Commission: ₹{reviewItems.reduce((s, i) => s + (i.platform_commission || 0), 0).toFixed(2)}</span>
                </div>
                <button
                  className="oor-submit-btn"
                  onClick={handleSubmitOnlineSales}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : `Submit ${reviewItems.filter(i => i.is_matched).length} Items`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* =================== MISSING INGREDIENTS MODAL =================== */}
      {showMissingModal && (
        <div className="oor-modal-overlay" onClick={() => setShowMissingModal(false)}>
          <div className="oor-modal-container" onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>⚠️ Insufficient Stock</h3>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>Some ingredients do not have enough stock to deduct.</p>
            <div style={{ maxHeight: 300, overflow: 'auto', marginBottom: 16 }}>
              {missingIngredients.map((item, idx) => (
                <div key={idx} style={{ marginBottom: 12, padding: 12, background: '#fef3c7', borderRadius: 8, fontSize: 13 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{item.itemName}</div>
                  {item.missingComponents?.map((comp, ci) => (
                    <div key={ci} style={{ marginLeft: 12 }}>
                      {comp.ingredientName}: need {comp.required} {comp.unit}, have {comp.available}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 14 }}>
              <input type="checkbox" checked={allowNegativeStock} onChange={e => setAllowNegativeStock(e.target.checked)} />
              Allow Negative Stock (proceed anyway)
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="oor-modal-cancel" onClick={() => setShowMissingModal(false)}>Cancel</button>
              <button className="oor-modal-confirm" onClick={() => { setShowMissingModal(false); handleSubmitOnlineSales(); }}>Proceed</button>
            </div>
          </div>
        </div>
      )}

      {/* =================== SWIGGY ORDER REPORT REVIEW MODAL =================== */}
      {showOrderReportModal && orderReport && (
        <div className="oor-modal-overlay" onClick={closeOrderReportModal}>
          <div className="oor-modal-container oor-or-modal" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="oor-or-head">
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="oor-platform-badge swiggy">🟠 Swiggy Order Report</span>
                </h3>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
                  {orderReport.summary?.date_from === orderReport.summary?.date_to
                    ? formatDateDisplayDMY(orderReport.summary?.date_from)
                    : `${formatDateDisplayDMY(orderReport.summary?.date_from)} → ${formatDateDisplayDMY(orderReport.summary?.date_to)}`}
                </div>
              </div>
              <button className="oor-or-close" onClick={closeOrderReportModal} aria-label="Close">
                <XCircle size={22} />
              </button>
            </div>

            {/* Summary chips */}
            <div className="oor-or-chips">
              <span className="oor-or-chip">{orderReport.summary?.order_count || 0} orders</span>
              <span className="oor-or-chip">{orderReport.summary?.total_items_sold || 0} items</span>
              <span className="oor-or-chip">₹{(orderReport.summary?.total_revenue || 0).toFixed(0)} gross</span>
              <span className="oor-or-chip">{orderReport.matchSummary?.matched || 0} matched · {orderReport.matchSummary?.unmatched || 0} unmatched</span>
              {orderReport.summary?.cancelled_count > 0 && (
                <span className="oor-or-chip warn">{orderReport.summary.cancelled_count} cancelled</span>
              )}
              {orderReport.summary?.truncated_count > 0 && (
                <span className="oor-or-chip warn">{orderReport.summary.truncated_count} incomplete</span>
              )}
            </div>

            {/* Results view (after submit) */}
            {orResults ? (
              <div className="oor-or-results">
                <div className="oor-or-results-summary">
                  <span className="oor-or-chip ok">✓ {orResults.summary.imported} imported</span>
                  <span className="oor-or-chip">↷ {orResults.summary.skipped} skipped</span>
                  {orResults.summary.errors > 0 && <span className="oor-or-chip warn">⚠ {orResults.summary.errors} errors</span>}
                </div>
                <div className="oor-or-results-list">
                  {orResults.results.map((r, i) => (
                    <div key={i} className={`oor-or-result-row ${r.status}`}>
                      <span>{formatDateDisplayDMY(r.order_date)}</span>
                      <span className="oor-or-result-status">
                        {r.status === 'imported' ? `✓ ₹${(r.total_amount || 0).toFixed(0)} · COGS ₹${(r.cogs || 0).toFixed(0)}`
                          : r.status === 'skipped' ? (r.reason === 'already_imported' ? '↷ already imported' : '↷ skipped')
                          : `⚠ ${r.reason}`}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="oor-or-footer">
                  <button className="oor-submit-btn" onClick={closeOrderReportModal}>Done</button>
                </div>
              </div>
            ) : (
              <>
                {/* Per-date list */}
                <div className="oor-or-dates">
                  {(orderReport.dateSummary || []).map(ds => {
                    const expanded = !!orExpandedDates[ds.order_date];
                    const dayOrders = orderReport.orders
                      .map((o, idx) => ({ o, idx }))
                      .filter(({ o }) => o.order_date === ds.order_date);
                    return (
                      <div key={ds.order_date} className={`oor-or-date ${ds.already_imported ? 'imported' : ''}`}>
                        <div
                          className="oor-or-date-head"
                          onClick={() => setOrExpandedDates(p => ({ ...p, [ds.order_date]: !p[ds.order_date] }))}
                        >
                          <ChevronDown size={16} style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s', flexShrink: 0 }} />
                          <span className="oor-or-date-label">{formatDateDisplayDMY(ds.order_date)}</span>
                          <span className="oor-or-date-meta">{ds.order_count} orders · {ds.item_count} items · ₹{ds.gross.toFixed(0)}</span>
                          {ds.already_imported
                            ? <span className="oor-status-badge mismatch">Already imported</span>
                            : <span className="oor-status-badge matched">New</span>}
                        </div>

                        {expanded && (
                          <div className="oor-or-date-body">
                            {dayOrders.map(({ o, idx }) => (
                              <div key={idx} className={`oor-or-order ${o.is_cancelled ? 'cancelled' : ''} ${o.is_truncated ? 'truncated' : ''}`}>
                                <div className="oor-or-order-head">
                                  <span>{o.order_time || '--:--'}</span>
                                  <span>₹{(o.bill_amount || 0).toFixed(0)}</span>
                                  {o.is_cancelled && <span className="oor-status-badge mismatch">Cancelled</span>}
                                  {o.is_truncated && <span className="oor-status-badge unmatched">⚠ {o.missing_count || '?'} item(s) / ₹{(o.missing_amount || 0).toFixed(0)} missing</span>}
                                </div>
                                {o.items.map((it, ii) => (
                                  <div key={ii} className="oor-or-item">
                                    <span className={`oor-or-dot ${it.is_matched ? 'ok' : 'bad'}`} />
                                    <span className="oor-or-item-name">
                                      {it.item_name}{it.variant ? ` (${it.variant})` : ''}
                                      {it.is_matched && it.matched_name && it.matched_name.toLowerCase() !== it.item_name.toLowerCase() &&
                                        <span className="oor-or-item-match"> → {it.matched_name}</span>}
                                      {!it.is_matched && <span className="oor-or-item-unmatched"> · not in menu</span>}
                                    </span>
                                    <span className="oor-or-item-qty">×{it.quantity}</span>
                                    <span className="oor-or-item-price">₹{(it.true_selling_price || 0).toFixed(0)}</span>
                                  </div>
                                ))}
                                {/* Manually-added items */}
                                {(orManualAdds[idx] || []).map((m, mi) => (
                                  <div key={`m${mi}`} className="oor-or-item manual">
                                    <span className="oor-or-dot ok" />
                                    <span className="oor-or-item-name">{m.item_name} <span className="oor-or-item-match">· added</span></span>
                                    <span className="oor-or-item-qty">×{m.quantity}</span>
                                    <span className="oor-or-item-price">₹{Number(m.price).toFixed(0)}</span>
                                    <button className="oor-or-item-remove" onClick={() => removeManualItem(idx, mi)} title="Remove">×</button>
                                  </div>
                                ))}
                                {o.is_truncated && !o.is_cancelled && !ds.already_imported && (
                                  <OrManualAddRow menuList={orMenuList} onAdd={(entry) => addManualItem(idx, entry)} />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="oor-or-footer">
                  <label className="oor-or-neg">
                    <input type="checkbox" checked={allowNegativeStock} onChange={e => setAllowNegativeStock(e.target.checked)} />
                    Allow negative stock
                  </label>
                  <div style={{ flex: 1 }} />
                  <button className="oor-modal-cancel" onClick={closeOrderReportModal}>Cancel</button>
                  <button className="oor-submit-btn" onClick={handleSubmitOrderReport} disabled={orSubmitting || newDatesCount === 0}>
                    {orSubmitting ? 'Importing…' : `Import ${newDatesCount} new date${newDatesCount === 1 ? '' : 's'}`}
                  </button>
                </div>
              </>
            )}

            {/* Shared datalist for manual completion */}
            <datalist id="or-menu-list">
              {orMenuList.map(m => <option key={m.menu_item_id} value={m.name} />)}
            </datalist>
          </div>
        </div>
      )}

      {/* =================== REVERT MODAL (LOCALHOST ONLY) =================== */}
      {showRevertModal && isLocalhost && (
        <div className="oor-modal-overlay" onClick={() => setShowRevertModal(false)}>
          <div className="oor-modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>🔄 Revert Online Order</h3>
            {revertLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>Loading...</div>
            ) : revertList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>No recent online orders to revert.</div>
            ) : (
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                 {revertList.map(order => {
                   const isConfirming = revertTargetId === order.online_order_id;
                   const isReverting = revertingId === order.online_order_id;
                   return (
                   <div key={order.online_order_id} className="oor-revert-card" style={{
                     border: isConfirming ? '2px solid #7c3aed' : undefined
                   }}>
                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                       <div>
                         <strong>Order #{order.online_order_id}</strong>
                         {order.platform_source && (
                           <span className={`oor-platform-badge ${order.platform_source}`} style={{ marginLeft: 8, fontSize: 11 }}>
                             {order.platform_source}
                           </span>
                         )}
                       </div>
                       <span style={{ fontSize: 12, color: '#6b7280' }}>{formatDateDisplayDMY(order.order_date)}</span>
                     </div>
                     <div style={{ fontSize: 13, marginBottom: 8 }}>
                       ₹{parseFloat(order.line_items_total || order.total_amount || 0).toFixed(2)} · {(order.line_items || []).length} items
                       {order.platform_commission_total > 0 && ` · Commission: ₹${parseFloat(order.platform_commission_total).toFixed(2)}`}
                     </div>
                     {!isConfirming ? (
                       <button
                         className="oor-revert-action-btn"
                         disabled={!!revertingId}
                         onClick={() => { setRevertTargetId(order.online_order_id); setRevertError(null); }}
                       >
                         Revert This Order
                       </button>
                     ) : (
                       <div>
                         <p style={{ color: '#7c3aed', fontWeight: 700, fontSize: 13, marginBottom: 10, textAlign: 'center' }}>
                           Are you sure? This action cannot be undone.
                         </p>
                         <div style={{ display: 'flex', gap: 8 }}>
                           <button className="oor-modal-cancel" style={{ flex: 1 }} disabled={isReverting}
                             onClick={() => { setRevertTargetId(null); setRevertError(null); }}>Cancel</button>
                           <button className="oor-modal-confirm" style={{ flex: 1 }} disabled={isReverting}
                             onClick={() => handleRevertOrder(order.online_order_id)}>
                             {isReverting ? 'Reverting...' : '✓ Yes, Revert'}
                           </button>
                         </div>
                       </div>
                     )}
                   </div>
                   );
                 })}
              </div>
            )}
            {revertError && (
              <div className="oor-toast" style={{ marginTop: 12, background: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                ❌ {revertError}
              </div>
            )}
            {revertSuccess && (
              <div className="oor-toast oor-toast-success" style={{ marginTop: 12 }}>
                <Check size={16} /> {revertSuccess}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="oor-modal-cancel" onClick={() => setShowRevertModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

/**
 * Shared with the Banquet Orders report, which mirrors this page's layout.
 *
 * Exported rather than copied so the period control, the date maths and the
 * item avatar cannot drift between two screens that are meant to look and
 * behave identically. This is an ADDITIVE export: nothing above it changes, and
 * this page's own behaviour is untouched.
 */
export {
  OnlinePeriodFilter,
  ItemAvatar,
  getDateRangeForPeriod,
  formatDateParam,
  formatDateDisplayDMY,
  formatRangeDisplayDMY,
  // Exported for tests of what the page shows; nothing else imports them.
  PulseHeader,
  ItemSalesReport,
  costBannerFor,
  mergeItemsAcrossPlatforms,
};

export default OnlineOrdersReport;
